/**
 * ew-konfigurator.js — EW Konfigurator (Shop-Theme airtec-b2b-theme)
 *
 * GENERIERTES ARTEFAKT — nicht von Hand ändern (Stufe 2, Phase S1).
 * Quelle:  Repo airtec-angebotstool
 *            engine/ew-engine.mjs      Rechenkern (Sektionen A–F), gemeinsam mit dem Angebotstool
 *            adapter-shop/*.js         Shop-Adapter (i18n-Helfer, skuIndex, Glue, UI G–K)
 * Build:   npm run build:theme  →  dist/ew-konfigurator.js  →  Kopie nach theme/assets/
 * Beweis:  npm run parity (Engine == Artefakt), Theme-Golden dev/tests/ew-konfigurator
 * Quellen: engine 7e1f074b75bd · helpers bd1958ddfb6e · skuIndex e84db7fecbdf · glue 790408523a64 · modules c6f6bf664f3d · ui 68d227f68b00 · svg e01b5f35eb4c · beton a3cb772238ec
 */

'use strict';

// ══════════════════════════════════════════════════════════════════
// i18n — Übersetzungshelfer (liest window.EW_I18N, injiziert via Liquid)
// ══════════════════════════════════════════════════════════════════
function t(key, vars) {
  const i18n = window.EW_I18N || {};
  let str = i18n[key] || key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.replace(new RegExp(`__${k.toUpperCase()}__`, 'g'), v);
    }
  }
  return str;
}

/** Escaped HTML-Sonderzeichen für sichere innerHTML-/Attribut-Interpolation */
function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

/**
 * ew-engine.mjs — EW-Konfigurator-Rechenkern (Airtec Angebotstool)
 *
 * Extrahiert aus dem Shop-Theme (airtec-b2b-theme, assets/ew-konfigurator.js,
 * Sektionen A–F). Herkunft/Version: siehe UPSTREAM.md — Änderungen am
 * Shop-Konfigurator meldet der Drift-Wächter, danach hier bewusst nachziehen.
 *
 * Dependency-freies ES-Modul, läuft unverändert in Node UND im Browser.
 * Alle Umgebungs-Abhängigkeiten des Originals (window.EW_I18N, EW_LOCALE,
 * skuIndex, EW_PRODUCTS_LIQUID_COUNT) sind als env-Parameter injizierbar;
 * die Defaults reproduzieren exakt das Verhalten des Shop-Monolithen ohne
 * geladenen Produktindex (t = Key-Echo, locale de, getVariant = null).
 *
 * Rechenlogik, SKU_MAP und Zeilen-Shape sind bewusst UNVERÄNDERT übernommen
 * (Paritäts-Harness: test/parity.mjs vergleicht gegen den Original-Monolithen).
 *
 * UI-INVARIANTEN — Regeln, die der Shop nur über das Formular erzwingt
 * (updateFormDisplay/initForm); ein Adapter muss sie VOR buildBom sicherstellen:
 *   1. anwendung 'pellets'  ⇒ diameter ≤ 100 (validateState meldet, UI resettet)
 *   2. diameter 350         ⇒ kaminabdeckplatte = false (keine 350er-SKUs)
 *   3. isolation ∉ {30mm,50mm} ⇒ leichtbauschacht = false
 *   4. kaminhut 'sirius'    ⇒ kaminhutSurface ≠ 'inox-gebuestet'
 *   5. kaminhoehe: Meter mit max. 1 Dezimalstelle, > 0
 */


// ══════════════════════════════════════════════════════════════════
// A. KONSTANTEN & ROLLE-MAPPING (verbatim aus dem Theme)
// ══════════════════════════════════════════════════════════════════

const EW = Object.freeze({
  /** Alle gültigen Durchmesser in mm */
  DIAMETERS: [80, 100, 130, 150, 180, 200, 250, 300, 350],

  /** Effektive Einbaulänge je Nennlänge (Nennlänge - 60mm Einstecktiefe) */
  EFF_LENGTHS: { 1000: 940, 500: 440, 250: 190 },

  /** T-Klasse je Anwendung */
  T_CLASSES: { holz: 'T400', oel: 'T200', gas: 'T120', pellets: 'T400' },
});

const SKU_MAP = {
  // 1. ROHRE EW (Einwandig)
  pipe: {
    '0.6': {
      1000: { 80: '101-0106-1001-10.080', 100: '101-0106-1001-10.100', 130: '101-0106-1001-10.130', 150: '101-0106-1001-10.150', 180: '101-0106-1001-10.180', 200: '101-0106-1001-10.200', 250: '101-0106-1001-10.250', 300: '101-0106-1001-10.300', 350: '101-0106-1001-10.350' },
      500:  { 80: '101-0106-1001-50.080', 100: '101-0106-1001-50.100', 130: '101-0106-1001-50.130', 150: '101-0106-1001-50.150', 180: '101-0106-1001-50.180', 200: '101-0106-1001-50.200', 250: '101-0106-1001-50.250', 300: '101-0106-1001-50.300', 350: '101-0106-1001-50.350' },
      250:  { 80: '101-0106-1001-25.080', 100: '101-0106-1001-25.100', 130: '101-0106-1001-25.130', 150: '101-0106-1001-25.150', 180: '101-0106-1001-25.180', 200: '101-0106-1001-25.200', 250: '101-0106-1001-25.250', 300: '101-0106-1001-25.300', 350: '101-0106-1001-25.350' }
    },
    '1.0': {
      1000: { 80: '101-0110-1001-10.080', 100: '101-0110-1001-10.100', 130: '101-0110-1001-10.130', 150: '101-0110-1001-10.150', 180: '101-0110-1001-10.180', 200: '101-0110-1001-10.200', 250: '101-0110-1001-10.250', 300: '101-0110-1001-10.300', 350: '101-0110-1001-10.350' },
      500:  { 80: '101-0110-1001-50.080', 100: '101-0110-1001-50.100', 130: '101-0110-1001-50.130', 150: '101-0110-1001-50.150', 180: '101-0110-1001-50.180', 200: '101-0110-1001-50.200', 250: '101-0110-1001-50.250', 300: '101-0110-1001-50.300', 350: '101-0110-1001-50.350' },
      250:  { 80: '101-0110-1001-25.080', 100: '101-0110-1001-25.100', 130: '101-0110-1001-25.130', 150: '101-0110-1001-25.150', 180: '101-0110-1001-25.180', 200: '101-0110-1001-25.200', 250: '101-0110-1001-25.250', 300: '101-0110-1001-25.300', 350: '101-0110-1001-25.350' }
    }
  },

  // 2. KERAMIKROHRE (19mm Isolation)
  pipe_19mm: {
    1000: { 80: '104-0112-1001-10.080', 100: '104-0112-1001-10.100', 130: '104-0112-1001-10.130', 150: '104-0112-1001-10.150', 180: '104-0112-1001-10.180', 200: '104-0112-1001-10.200', 250: '104-0112-1001-10.250', 300: '104-0112-1001-10.300', 350: '104-0112-1001-10.350' },
    500:  { 80: '104-0112-1001-50.080', 100: '104-0112-1001-50.100', 130: '104-0112-1001-50.130', 150: '104-0112-1001-50.150', 180: '104-0112-1001-50.180', 200: '104-0112-1001-50.200', 250: '104-0112-1001-50.250', 300: '104-0112-1001-50.300', 350: '104-0112-1001-50.350' }
  },

  // 3. ISOLATION
  iso_30mm: { 80: '130-1503-6001-30.080', 100: '130-1503-6001-30.100', 130: '130-1503-6001-30.130', 150: '130-1503-6001-30.150', 180: '130-1503-6001-30.180', 200: '130-1503-6001-30.200', 250: '130-1503-6001-30.250', 300: '130-1503-6001-30.300', 350: '130-1503-6001-30.350' },
  iso_50mm: { 80: '130-1503-6001-50.080', 100: '130-1503-6001-50.100', 130: '130-1503-6001-50.130', 150: '130-1503-6001-50.150', 180: '130-1503-6001-50.180', 200: '130-1503-6001-50.200', 250: '130-1503-6001-50.250', 300: '130-1503-6001-50.300', 350: '130-1503-6001-50.350' },
  schuetti: '131-1503-6301-10',

  // 4. DOPPELFUTTERROHR
  dfr_lang:   { 80: '101-0106-2001-33.080', 100: '101-0106-2001-33.100', 130: '101-0106-2001-33.130', 150: '101-0106-2001-33.150', 180: '101-0106-2001-33.180', 200: '101-0106-2001-33.200', 250: '101-0106-2001-33.250', 300: '101-0106-2001-33.300', 350: '101-0106-2001-33.350' },
  dfr_mittel: { 80: '101-0106-2001-20.080', 100: '101-0106-2001-20.100', 130: '101-0106-2001-20.130', 150: '101-0106-2001-20.150', 180: '101-0106-2001-20.180', 200: '101-0106-2001-20.200', 250: '101-0106-2001-20.250', 300: '101-0106-2001-20.300', 350: '101-0106-2001-20.350' },
  dfr_kurz:   { 80: '101-0106-2001-05.080', 100: '101-0106-2001-05.100', 130: '101-0106-2001-05.130', 150: '101-0106-2001-05.150', 180: '101-0106-2001-05.180', 200: '101-0106-2001-05.200', 250: '101-0106-2001-05.250', 300: '101-0106-2001-05.300', 350: '101-0106-2001-05.350' },

  // 5. RUSSTOPF
  russtopf_geschlossen:          { 80: '101-0106-2201-01.080', 100: '101-0106-2201-01.100', 130: '101-0106-2201-01.130', 150: '101-0106-2201-01.150', 180: '101-0106-2201-01.180', 200: '101-0106-2201-01.200', 250: '101-0106-2201-01.250', 300: '101-0106-2201-01.300', 350: '101-0106-2201-01.350' },
  russtopf_skof:                 { 80: '101-0106-2203-01.080', 100: '101-0106-2203-01.100', 130: '101-0106-2203-01.130', 150: '101-0106-2203-01.150', 180: '101-0106-2203-01.180', 200: '101-0106-2203-01.200', 250: '101-0106-2203-01.250', 300: '101-0106-2203-01.300', 350: '101-0106-2203-01.350' },
  russtopf_kondensat_unten:      { 80: '101-0106-2202-01.080', 100: '101-0106-2202-01.100', 130: '101-0106-2202-01.130', 150: '101-0106-2202-01.150', 180: '101-0106-2202-01.180', 200: '101-0106-2202-01.200', 250: '101-0106-2202-01.250', 300: '101-0106-2202-01.300', 350: '101-0106-2202-01.350' },
  russtopf_kondensat_seitlich:   { 80: '101-0106-2202-02.080', 100: '101-0106-2202-02.100', 130: '101-0106-2202-02.130', 150: '101-0106-2202-02.150', 180: '101-0106-2202-02.180', 200: '101-0106-2202-02.200', 250: '101-0106-2202-02.250', 300: '101-0106-2202-02.300', 350: '101-0106-2202-02.350' },
  russtopf_skof_kondensat_unten: { 80: '101-0106-2204-01.080', 100: '101-0106-2204-01.100', 130: '101-0106-2204-01.130', 150: '101-0106-2204-01.150', 180: '101-0106-2204-01.180', 200: '101-0106-2204-01.200', 250: '101-0106-2204-01.250', 300: '101-0106-2204-01.300', 350: '101-0106-2204-01.350' },
  russtopf_skof_kondensat_seit:  { 80: '101-0106-2204-02.080', 100: '101-0106-2204-02.100', 130: '101-0106-2204-02.130', 150: '101-0106-2204-02.150', 180: '101-0106-2204-02.180', 200: '101-0106-2204-02.200', 250: '101-0106-2204-02.250', 300: '101-0106-2204-02.300', 350: '101-0106-2204-02.350' },

  // 6. REINIGUNGSELEMENT
  re_standard: { 80: '101-0106-1702-05.080', 100: '101-0106-1702-05.100', 130: '101-0106-1702-05.130', 150: '101-0106-1702-05.150', 180: '101-0106-1702-05.180', 200: '101-0106-1702-05.200', 250: '101-0106-1702-05.250', 300: '101-0106-1702-05.300', 350: '101-0106-1702-05.350' },
  re_alt:      { 80: '101-0106-1701-02.080', 100: '101-0106-1701-02.100', 130: '101-0106-1701-02.130', 150: '101-0106-1701-02.150', 180: '101-0106-1701-02.180', 200: '101-0106-1701-02.200', 250: '101-0106-1701-02.250', 300: '101-0106-1701-02.300', 350: '101-0106-1701-02.350' }, // Variante mit Viton Dichtung

  // 7. T-STÜCKE & T-KLIX
  tstueck_90: { 80: '101-0106-2101-90.080', 100: '101-0106-2101-90.100', 130: '101-0106-2101-90.130', 150: '101-0106-2101-90.150', 180: '101-0106-2101-90.180', 200: '101-0106-2101-90.200', 250: '101-0106-2101-90.250', 300: '101-0106-2101-90.300', 350: '101-0106-2101-90.350' },
  tstueck_87: { 80: '101-0106-2101-87.080', 100: '101-0106-2101-87.100', 130: '101-0106-2101-87.130', 150: '101-0106-2101-87.150', 180: '101-0106-2101-87.180', 200: '101-0106-2101-87.200', 250: '101-0106-2101-87.250', 300: '101-0106-2101-87.300', 350: '101-0106-2101-87.350' },
  tstueck_60: { 80: '101-0106-2101-60.080', 100: '101-0106-2101-60.100', 130: '101-0106-2101-60.130', 150: '101-0106-2101-60.150', 180: '101-0106-2101-60.180', 200: '101-0106-2101-60.200', 250: '101-0106-2101-60.250', 300: '101-0106-2101-60.300', 350: '101-0106-2101-60.350' },
  tstueck_45: { 80: '101-0106-2101-45.080', 100: '101-0106-2101-45.100', 130: '101-0106-2101-45.130', 150: '101-0106-2101-45.150', 180: '101-0106-2101-45.180', 200: '101-0106-2101-45.200', 250: '101-0106-2101-45.250', 300: '101-0106-2101-45.300', 350: '101-0106-2101-45.350' },
  tstueck_90_dfr: { 80: '101-0106-2103-90.080', 100: '101-0106-2103-90.100', 130: '101-0106-2103-90.130', 150: '101-0106-2103-90.150', 180: '101-0106-2103-90.180', 200: '101-0106-2103-90.200', 250: '101-0106-2103-90.250', 300: '101-0106-2103-90.300', 350: '101-0106-2103-90.350' },
  tklix_90: { 80: '101-0106-2122-90.080', 100: '101-0106-2122-90.100', 130: '101-0106-2122-90.130', 150: '101-0106-2122-90.150', 180: '101-0106-2122-90.180', 200: '101-0106-2122-90.200', 250: '101-0106-2122-90.250', 300: '101-0106-2122-90.300', 350: '101-0106-2122-90.350' },

  silikon_dichtung: { 80: '135-1505-8502-20.080', 100: '135-1505-8502-20.100', 130: '135-1505-8502-20.130', 150: '135-1505-8502-20.150', 180: '135-1505-8502-20.180', 200: '135-1505-8502-20.200', 250: '135-1505-8502-20.250', 300: '135-1505-8502-20.300', 350: '135-1505-8502-20.350' },
  klemmband:        { 80: '101-0106-1201-01.080', 100: '101-0106-1201-01.100', 130: '101-0106-1201-01.130', 150: '101-0106-1201-01.150', 180: '101-0106-1201-01.180', 200: '101-0106-1201-01.200', 250: '101-0106-1201-01.250', 300: '101-0106-1201-01.300', 350: '101-0106-1201-01.350' },
  alu_tape_50mm: '114-1504-6550-50',
  alu_tape_100mm: '114-1504-6550-10',

  // 8. SCHACHTELEMENTE (30mm und 50mm)
  schacht: {
    1000: {
      '30mm': { '160 x 160 mm': '140-1215-1002-10.160', '190 x 190 mm': '140-1215-1002-10.190', '210 x 210 mm': '140-1215-1002-10.210', '230 x 230 mm': '140-1215-1002-10.230', '260 x 260 mm': '140-1215-1002-10.260', '310 x 310 mm': '140-1215-1002-10.310', '360 x 360 mm': '140-1215-1002-10.360' },
      '50mm': { '190 x 190 mm': '140-1215-1002-10.190', '210 x 210 mm': '140-1215-1002-10.210', '240 x 240 mm': '140-1215-1002-10.240', '260 x 260 mm': '140-1215-1002-10.260', '310 x 310 mm': '140-1215-1002-10.310', '360 x 360 mm': '140-1215-1002-10.360' }
    },
    500: {
      '30mm': { '160 x 160 mm': '140-1215-1002-50.160', '190 x 190 mm': '140-1215-1002-50.190', '210 x 210 mm': '140-1215-1002-50.210', '230 x 230 mm': '140-1215-1002-50.230', '260 x 260 mm': '140-1215-1002-50.260', '310 x 310 mm': '140-1215-1002-50.310', '360 x 360 mm': '140-1215-1002-50.360' },
      '50mm': { '190 x 190 mm': '140-1215-1002-50.190', '210 x 210 mm': '140-1215-1002-50.210', '240 x 240 mm': '140-1215-1002-50.240', '260 x 260 mm': '140-1215-1002-50.260', '310 x 310 mm': '140-1215-1002-50.310', '360 x 360 mm': '140-1215-1002-50.360' }
    },
    250: {
      '30mm': { '160 x 160 mm': '140-1215-1002-25.160', '190 x 190 mm': '140-1215-1002-25.190', '210 x 210 mm': '140-1215-1002-25.210', '230 x 230 mm': '140-1215-1002-25.230', '260 x 260 mm': '140-1215-1002-25.260', '310 x 310 mm': '140-1215-1002-25.310', '360 x 360 mm': '140-1215-1002-25.360' },
      '50mm': { '190 x 190 mm': '140-1215-1002-25.190', '210 x 210 mm': '140-1215-1002-25.210', '240 x 240 mm': '140-1215-1002-25.240', '260 x 260 mm': '140-1215-1002-25.260', '310 x 310 mm': '140-1215-1002-25.310', '360 x 360 mm': '140-1215-1002-25.360' }
    }
  },

  // 8b. KLEBER FÜR LEICHTBAUSCHACHT
  lbs_kleber: '101-1215-8901-01',
  lbs_schrauben: '101-1215-5901-01',

  // 9. KAMINABDECKPLATTE (inkl. Dila & Stutzen)
  kaminabdeckplatte: {
    ew: {
      'inox-matt': { 80: '101-1572-01-43.080', 100: '101-1572-01-43.100', 130: '101-1572-01-43.130', 150: '101-1572-01-43.150', 180: '101-1572-01-43.180', 200: '101-1572-01-43.200', 250: '101-1572-01-43.250', 300: '101-1572-01-43.300' },
      'kupfer':    { 80: '102-1572-01-43.080', 100: '102-1572-01-43.100', 130: '102-1572-01-43.130', 150: '102-1572-01-43.150', 180: '102-1572-01-43.180', 200: '102-1572-01-43.200', 250: '102-1572-01-43.250', 300: '102-1572-01-43.300' }
    },
    dw: {
      'inox-matt': { 80: '101-1572-50-48.080', 100: '101-1572-50-48.100', 130: '101-1572-50-48.130', 150: '101-1572-50-48.150', 180: '101-1572-50-48.180', 200: '101-1572-50-48.200', 250: '101-1572-50-48.250', 300: '101-1572-50-48.300' },
      'kupfer':    { 80: '102-1572-50-48.080', 100: '102-1572-50-48.100', 130: '102-1572-50-48.130', 150: '102-1572-50-48.150', 180: '102-1572-50-48.180', 200: '102-1572-50-48.200', 250: '102-1572-50-48.250', 300: '102-1572-50-48.300' }
    }
  },

  // 10. DILATABSCHLUSS
  dilatabschluss: {
    '32.5mm': {
      'inox-matt': { 80: '101-1501-7101-32.080', 100: '101-1501-7101-32.100', 130: '101-1501-7101-32.130', 150: '101-1501-7101-32.150', 180: '101-1501-7101-32.180', 200: '101-1501-7101-32.200', 250: '101-1501-7101-32.250', 300: '101-1501-7101-32.300', 350: '101-1501-7101-32.350' },
      'kupfer':    { 80: '102-1501-7101-32.080', 100: '102-1501-7101-32.100', 130: '102-1501-7101-32.130', 150: '102-1501-7101-32.150', 180: '102-1501-7101-32.180', 200: '102-1501-7101-32.200', 250: '102-1501-7101-32.250', 300: '102-1501-7101-32.300', 350: '102-1501-7101-32.350' }
    },
    '50mm': {
      'inox-matt': { 80: '101-1501-7101-50.080', 100: '101-1501-7101-50.100', 130: '101-1501-7101-50.130', 150: '101-1501-7101-50.150', 180: '101-1501-7101-50.180', 200: '101-1501-7101-50.200', 250: '101-1501-7101-50.250', 300: '101-1501-7101-50.300', 350: '101-1501-7101-50.350' },
      'kupfer':    { 80: '102-1501-7101-50.080', 100: '102-1501-7101-50.100', 130: '102-1501-7101-50.130', 150: '102-1501-7101-50.150', 180: '102-1501-7101-50.180', 200: '102-1501-7101-50.200', 250: '102-1501-7101-50.250', 300: '102-1501-7101-50.300', 350: '102-1501-7101-50.350' }
    }
  },

  // 11. KAMINHUT
  kaminhut: {
    'airnox': {
      'inox-matt': { 80: '101-1501-7001-01.080', 100: '101-1501-7001-01.100', 130: '101-1501-7001-01.130', 150: '101-1501-7001-01.150', 180: '101-1501-7001-01.180', 200: '101-1501-7001-01.200', 250: '101-1501-7001-01.250', 300: '101-1501-7001-01.300', 350: '101-1501-7001-01.350' },
      'kupfer':    { 80: '102-1501-7001-01.080', 100: '102-1501-7001-01.100', 130: '102-1501-7001-01.130', 150: '102-1501-7001-01.150', 180: '102-1501-7001-01.180', 200: '102-1501-7001-01.200', 250: '102-1501-7001-01.250', 300: '102-1501-7001-01.300', 350: '102-1501-7001-01.350' }
    },
    'airnox-turbo': {
      'inox-matt': { 80: '101-1501-7001-02.080', 100: '101-1501-7001-02.100', 130: '101-1501-7001-02.130', 150: '101-1501-7001-02.150', 180: '101-1501-7001-02.180', 200: '101-1501-7001-02.200', 250: '101-1501-7001-02.250', 300: '101-1501-7001-02.300', 350: '101-1501-7001-02.350' },
      'kupfer':    { 80: '102-1501-7001-02.080', 100: '102-1501-7001-02.100', 130: '102-1501-7001-02.130', 150: '102-1501-7001-02.150', 180: '102-1501-7001-02.180', 200: '102-1501-7001-02.200', 250: '102-1501-7001-02.250', 300: '102-1501-7001-02.300', 350: '102-1501-7001-02.350' }
    },
    'sirius': {
      'inox-matt': { 80: '101-1501-7003-01.080', 100: '101-1501-7003-01.100', 130: '101-1501-7003-01.130', 150: '101-1501-7003-01.150', 180: '101-1501-7003-01.180', 200: '101-1501-7003-01.200', 250: '101-1501-7003-01.250', 300: '101-1501-7003-01.300', 350: '101-1501-7003-01.350' },
      'kupfer':    { 80: '102-1501-7003-01.080', 100: '102-1501-7003-01.100', 130: '102-1501-7003-01.130', 150: '102-1501-7003-01.150', 180: '102-1501-7003-01.180', 200: '102-1501-7003-01.200', 250: '102-1501-7003-01.250', 300: '102-1501-7003-01.300', 350: '102-1501-7003-01.350' }
    }
  }
};

// ── E3: Decken-/Dachdurchführung (Betonierung) — Ø-Suffix .080…350 wird angehängt.
//     Stand 18.09.2026 (Shop-Bestand + Entscheid Daniel): alle DW-Systeme führen gerade ohne (-2003-01) /
//     mit Schleudern (-2003-02) und schräg nach Gradmass ohne (-2004-GM) / mit (-2005-GM). Der Katalog-Befund
//     vom 17.09. («schräg nur mit Schleudern» bei DW 32.5/50) ist damit überholt. EI60 nur Ø100–200.
//     EW-Futterrohre (-2003-01 Flansch / -2003-02 Schrägschnitt) bleiben in der Tabelle, sind aber seit der Vereinfachung
//     vom 18.09. nicht mehr wählbar: dieser Abschnitt führt nur das DW-Doppelfutterrohr, das einwandige fragt Abschnitt 2 ab.
SKU_MAP.dfr_beton = {
  ew:    { flach: { ohne: '101-0106-2003-01', mit: '101-0106-2003-01' }, schraeg: { ohne: '101-0106-2003-02', mit: '101-0106-2003-02' } },
  dw325: { flach: { ohne: '101-0232-2003-01', mit: '101-0232-2003-02' }, schraeg: { ohne: '101-0232-2004-GM', mit: '101-0232-2005-GM' } },
  dw50:  { flach: { ohne: '101-0250-2003-01', mit: '101-0250-2003-02' }, schraeg: { ohne: '101-0250-2004-GM', mit: '101-0250-2005-GM' } },
  dw575: { flach: { ohne: '103-0357-2003-01', mit: '103-0357-2003-02' }, schraeg: { ohne: '103-0357-2004-GM', mit: '103-0357-2005-GM' } },
};
/** Klemmband des gewählten Systems — seit 18.09. nur auf Wunsch («Mit Klemmband?» bei Schleudern = nein), vorher je Betonierung eines */
SKU_MAP.klemmband_system = { ew: '101-0106-1201-01', dw325: '101-0232-1201-01', dw50: '101-0250-1201-01', dw575: '103-0357-1201-01' };
/** Leichtbeton Latermix Béton 1600, Sack 25 l — Test-SKU (PIM-Übergabe A1), noch nicht im Shop */
SKU_MAP.beton_sack = '132-1503-6303-01';
/** Durchmesser je Ausführung (EI60 nur 100–200) */
const DFR_BETON_DIAMETERS = {
  ew: [80, 100, 130, 150, 180, 200, 250, 300, 350],
  dw325: [80, 100, 130, 150, 180, 200, 250, 300, 350],
  dw50: [80, 100, 130, 150, 180, 200, 250, 300, 350],
  dw575: [100, 130, 150, 180, 200],
};

// ══════════════════════════════════════════════════════════════════
// B2. ROLE_LABELS — Lesbare Bezeichnungen (Fallback wenn Produkt fehlt)
// ══════════════════════════════════════════════════════════════════

const ROLE_LABELS_DE = {
  pipe_1000:                   'Rohr L=1000mm',
  pipe_500:                    'Rohr L=500mm',
  pipe_250:                    'Rohr L=250mm',
  pipe_19mm_1000:              '19mm Keramikrohr L=1000mm',
  pipe_19mm_500:               '19mm Keramikrohr L=500mm',
  iso_30mm:                    'Isolationsschale 30mm',
  iso_50mm:                    'Isolationsschale 50mm',
  schuetti:                    'Vermiculite 100L (Schüttisolation)',
  dfr_lang:                    'Doppelfutterrohr lang L=330mm',
  dfr_mittel:                  'Doppelfutterrohr mittel L=200mm',
  dfr_kurz:                    'Doppelfutterrohr kurz L=50mm',
  russtopf_geschlossen:         'Russtopf geschlossen',
  russtopf_skof:                'Russtopf für SKOF-Konsole',
  russtopf_kondensat_unten:     'Russtopf m. Kondensatablauf unten 1" Nippel',
  russtopf_kondensat_seitlich:  'Russtopf m. Kondensatablauf seitlich 1" Nippel',
  russtopf_skof_kondensat_unten:    'Russtopf SKOF m. Kondensatablauf unten',
  russtopf_skof_kondensat_seitlich: 'Russtopf SKOF m. Kondensatablauf seitlich',
  re_standard:                 'Reinigungselement m. Kasten + Deckel 210×140mm',
  re_alt:                      'Reinigungselement (Alternative)',
  tstueck_90:                  'T-Stück 90° m. Kondensatableiter',
  tstueck_87:                  'T-Stück 87° m. Kondensatableiter',
  tstueck_60:                  'T-Stück 60° m. Kondensatableiter',
  tstueck_45:                  'T-Stück 45° m. Kondensatableiter',
  tstueck_90_red:              'T-Stück 90° direkt reduziert',
  tstueck_90_dfr:              'T-Stück 90° m. Doppelfutterrohr',
  tstueck_90_dfr_red:          'T-Stück 90° m. Doppelfutterrohr reduziert',
  tklix_90:                    'T-Klix 90° m. Kondensatableiter',
  alu_tape_50mm:               'Aluminiumklebeband 50mm L:50m',
  alu_tape_100mm:              'Aluminiumklebeband 100mm L:50m',
  schacht_1000:                'Schachtelement 1000mm lose/verschraubt',
  schacht_500:                 'Schachtelement 500mm lose/verschraubt',
  schacht_250:                 'Schachtelement 250mm lose/verschraubt',
  silikon_dichtung:            'Dichtring Silikon bis 200°C',
  kaminabdeckplatte:           'Kaminabdeckplatte (Dila eckig m. EW-Stutzen)',
  kaminabdeckplatte_dw:        'Kaminabdeckplatte (Dila eckig m. DW-Stutzen)',
  kaminhut_airnox:             'Kaminhut Airnox',
  kaminhut_sirius:             'Kaminhut Sirius',
  dilatabschluss_32mm_inox:    'Dilatabschluss 32.5mm Isolation (Inox matt)',
  dilatabschluss_32mm_kupfer:  'Dilatabschluss 32.5mm Isolation (Kupfer)',
  dilatabschluss_50mm_inox:    'Dilatabschluss 50mm Isolation (Inox matt)',
  dilatabschluss_50mm_kupfer:  'Dilatabschluss 50mm Isolation (Kupfer)',
  klemmband:                   'Klemmband',
  lbs_kleber:                  'Kleber für Leichtbauschacht EI90',
  lbs_schrauben:               'Schnellbauschrauben phosphorosiert',
};

ROLE_LABELS_DE.dfr_beton       = 'Deckendurchlauf Doppelfutterrohr';
ROLE_LABELS_DE.klemmband_beton = 'Klemmband (Deckendurchlauf)';
ROLE_LABELS_DE.beton_sack      = 'Leichtbeton Latermix Béton 1600, Sack 25 l';

const ROLE_LABELS_FR = {
  pipe_1000:                   'Tube L=1000mm',
  pipe_500:                    'Tube L=500mm',
  pipe_250:                    'Tube L=250mm',
  pipe_19mm_1000:              'Tube céramique 19mm L=1000mm',
  pipe_19mm_500:               'Tube céramique 19mm L=500mm',
  iso_30mm:                    "Coquille d'isolation 30mm",
  iso_50mm:                    "Coquille d'isolation 50mm",
  schuetti:                    'Vermiculite 100L (isolation en vrac)',
  dfr_lang:                    'Manchette double longue L=330mm',
  dfr_mittel:                  'Manchette double moyenne L=200mm',
  dfr_kurz:                    'Manchette double courte L=50mm',
  russtopf_geschlossen:         'Sac à suie fermé',
  russtopf_skof:                'Sac à suie pour console SKOF',
  russtopf_kondensat_unten:     'Sac à suie avec évacuation condensats bas 1" nipple',
  russtopf_kondensat_seitlich:  'Sac à suie avec évacuation condensats latéral 1" nipple',
  russtopf_skof_kondensat_unten:    'Sac à suie SKOF avec évacuation condensats bas',
  russtopf_skof_kondensat_seitlich: 'Sac à suie SKOF avec évacuation condensats latéral',
  re_standard:                 "Élément d'inspection avec caisson + couvercle 210×140mm",
  re_alt:                      "Élément d'inspection (alternative)",
  tstueck_90:                  'Té 90° avec dérivateur de condensats',
  tstueck_87:                  'Té 87° avec dérivateur de condensats',
  tstueck_60:                  'Té 60° avec dérivateur de condensats',
  tstueck_45:                  'Té 45° avec dérivateur de condensats',
  tstueck_90_red:              'Té 90° réduit direct',
  tstueck_90_dfr:              'Té 90° avec manchette double',
  tstueck_90_dfr_red:          'Té 90° avec manchette double réduit',
  tklix_90:                    'T-Klix 90° avec dérivateur de condensats',
  alu_tape_50mm:               'Ruban aluminium 50mm L:50m',
  alu_tape_100mm:              'Ruban aluminium 100mm L:50m',
  schacht_1000:                'Gaine légère 1000mm dévissable',
  schacht_500:                 'Gaine légère 500mm dévissable',
  schacht_250:                 'Gaine légère 250mm dévissable',
  silikon_dichtung:            'Joint silicone jusqu’à 200°C',
  kaminabdeckplatte:           'Dila carrée avec raccord simple paroi',
  kaminabdeckplatte_dw:        'Dila carrée avec raccord double paroi',
  kaminhut_airnox:             'Cape Airnox',
  kaminhut_sirius:             'Cape Sirius',
  dilatabschluss_32mm_inox:    'Dilatabschluss 32.5mm isolation (inox mat)',
  dilatabschluss_32mm_kupfer:  'Dilatabschluss 32.5mm isolation (cuivre)',
  dilatabschluss_50mm_inox:    'Dilatabschluss 50mm isolation (inox mat)',
  dilatabschluss_50mm_kupfer:  'Dilatabschluss 50mm isolation (cuivre)',
  klemmband:                   'Bride de sécurité',
  lbs_kleber:                  'Colle pour gaine technique EI90',
  lbs_schrauben:               'Vis de montage phosphatée',
};

// SCHACHT_INNENMASS — Mapping (Ø mm × Isolation) → Innenmass-Variante
// Quelle: Tabelle "AIRNOX GT EI90" (Anhang 5)
ROLE_LABELS_FR.dfr_beton       = 'Passage de dalle manchette double';
ROLE_LABELS_FR.klemmband_beton = 'Bride (passage de dalle)';
ROLE_LABELS_FR.beton_sack      = 'Béton léger Latermix Béton 1600, sac 25 l';

const SCHACHT_INNENMASS = {
  '30mm': {
     80: '160 x 160 mm',
    100: '190 x 190 mm',
    130: '210 x 210 mm',
    150: '230 x 230 mm',
    180: '260 x 260 mm',
    200: '310 x 310 mm',
    250: '360 x 360 mm',
  },
  '50mm': {
     80: '190 x 190 mm',
    100: '210 x 210 mm',
    130: '240 x 240 mm',
    150: '260 x 260 mm',
    180: '310 x 310 mm',
    200: '310 x 310 mm',
    250: '360 x 360 mm',
  },
};

// ══════════════════════════════════════════════════════════════════
// C. STATE-DEFAULTS (identisch zum Initial-State des Shop-Monolithen)
// ══════════════════════════════════════════════════════════════════

function createDefaultState() {
  return {
    anwendung:              null,     // 'holz' | 'oel' | 'gas' | 'pellets'
    diameter:               null,     // 80 | 100 | 130 | 150 | 180 | 200 | 250 | 300 | 350
    kaminhoehe:             null,     // number (Meter, 1 Dezimalstelle)
    isolation:              'keine',  // 'keine' | '19mm' | '30mm' | '50mm'
    wandstaerke:            '0.6',    // '0.6' | '1.0' (nur wenn Isolation ≠ 19mm)
    schuettisolation:       true,
    doppelfutterrohr:       false,
    russtopf:               false,
    reinigungselement:      false,
    tstueck:                false,
    leichtbauschacht:       false,
    ofenanschluss:          false,
    ofenanschlussTyp:       null,     // 'steel' | 'pellet' (automatisch)
    diameterB:              null,     // Ø Abgang Ofen (optional, für reduzierte T-Stücke)
    kaminabdeckplatte:        false,
    kaminabdeckplatteStutzen: 'einwandig', // 'einwandig' | 'doppelwandig'
    kaminabdeckplatteSurface: null,   // 'inox-matt' | 'kupfer'
    kaminhut:                 'nein', // 'nein' | 'airnox' | 'airnox-turbo' | 'sirius'
    kaminhutSurface:          'inox-matt', // 'inox-matt' | 'inox-gebuestet' | 'kupfer'
    dilaRund:                 false,       // nur relevant wenn leichtbauschacht=true

    // ── E3 Abschnitt 3: Decken-/Dachdurchführung (Betonierung) ─────────
    // Fragefluss Daniel 18.09.2026: «Betonierung?» ja/nein, danach JE Decke/Dach ein eigener Datensatz
    // (createBetonierung) — «Decke/Dach hinzufügen» stellt dieselben Fragen erneut.
    betonierung:   false,   // Hauptschalter — ohne ihn liefert buildMountingBom nichts
    betonierungen: [],      // Liste von createBetonierung()-Datensätzen
  };
}

/** Standardhöhe H der Schleudern ab Ende Doppelfutterrohr (Daniel 18.09.: 120 statt 80), Bereich wie Produktseite */
const BETON_H_DEFAULT = 120;
const BETON_H_MIN = 30;
const BETON_H_MAX = 250;

/**
 * Ein Datensatz «Decke / Dach» = ein Doppelfutterrohr zum Einbetonieren (immer doppelwandig — das einwandige
 * Doppelfutterrohr fragt Abschnitt 2 «Komponenten» bereits ab). null = Frage noch nicht beantwortet.
 * `system: null` heisst «Vorschlag aus der Isolation» (betonSystemVorschlag) — eine Wahl des Kunden überschreibt ihn.
 * Decke/Dach flach ⇒ Doppelfutterrohr flach (Entscheid Daniel 17.09., keine Nachfrage); Decke/Dach schräg ⇒ Vorschlag schräg, änderbar.
 * `vorlage`: vorherige Betonierung — übernimmt System/Schleudern/H (im selben Haus meist gleich), nicht Form/Neigung/Masse.
 */
function createBetonierung(vorlage = null) {
  const v = vorlage || {};
  return {
    form:       null,                       // zu betonierende Decke / Dach: 'flach' | 'schraeg'
    dfr:        v.dfr ?? null,              // «Doppelfutterrohr zum Einbetonieren?» true | false — bei false nur Beton
    dfrForm:    null,                       // Ausführung Doppelfutterrohr: 'flach' | 'schraeg' · null = wie Decke/Dach (Vorschlag)
    neigungArt: 'grad',                     // 'grad' | 'prozent' | 'cm' (Gliedermeter / Meterwinkel)
    neigung:    null,                       // Zahl in der gewählten Einheit
    system:     v.system ?? null,           // 'dw325' | 'dw50' | 'dw575' (EI60) · null = Vorschlag aus der Isolation
    schleudern: v.schleudern ?? null,
    klemmband:  v.klemmband ?? null,        // nur bei Schleudern = nein: zusätzliches Klemmband als Verankerung im Beton (Daniel 18.09.)
    hoehe:      v.hoehe ?? BETON_H_DEFAULT, // mm: Schleudern bis Ende Doppelfutterrohr
    h: null, b: null, d: null,              // Deckendicke / Breite / Tiefe der Betonierung in mm
  };
}

/** Vorschlag fürs DW-System aus der Isolation von Abschnitt 1: 30 mm ⇒ DW 32.5, 50 mm ⇒ DW 50, sonst keiner (Daniel 18.09.) */
function betonSystemVorschlag(isolation) {
  return isolation === '30mm' ? 'dw325' : isolation === '50mm' ? 'dw50' : null;
}

// ══════════════════════════════════════════════════════════════════
// E. ROHR-KALKULATION (pure Funktionen, verbatim)
// ══════════════════════════════════════════════════════════════════

/**
 * Berechnet Rohrstückzahlen anhand Kaminhöhe.
 * Effektive Einbaulänge = Nennlänge - 60mm (Einstecktiefe).
 */
function calcPipes(heightMeters, is19mm = false) {
  const hm = Number(String(heightMeters).replace(',', '.'));
  if (!hm || hm <= 0 || isNaN(hm)) return null;

  const hoeheMm = Math.round(hm * 1000);
  const eff1000 = EW.EFF_LENGTHS[1000]; // 940
  const eff500  = EW.EFF_LENGTHS[500];  // 440
  const eff250  = EW.EFF_LENGTHS[250];  // 190

  let n1000 = Math.floor(hoeheMm / eff1000);
  let rest  = hoeheMm - n1000 * eff1000;
  let n500  = 0;
  let n250  = 0;

  if (rest <= 0) {
    // exakt gedeckt — nichts hinzufügen
  } else if (!is19mm && rest <= eff250) {
    n250 = 1;
  } else if (rest <= eff500) {
    n500 = 1;
  } else if (!is19mm && rest <= eff500 + eff250) {
    n500 = 1; n250 = 1;
  } else {
    n1000++;
  }

  return { n1000, n500, n250 };
}

/**
 * Berechnet Schachtelement-Stückzahlen analog calcPipes.
 * Nutzlängen: 1000mm→975mm | 500mm→475mm | 250mm→225mm
 */
function calcSchacht(heightMeters) {
  const hoeheMm = Math.round(Number(heightMeters) * 1000);
  const eff1000 = 975, eff500 = 475, eff250 = 225;
  let n1000 = Math.floor(hoeheMm / eff1000);
  let rest  = hoeheMm - n1000 * eff1000;
  let n500 = 0, n250 = 0;

  if (rest <= 0) {
    // exakt gedeckt
  } else if (rest <= eff250) {
    n250 = 1;
  } else if (rest <= eff500) {
    n500 = 1;
  } else if (rest <= eff500 + eff250) {
    n500 = 1; n250 = 1;
  } else {
    n1000++;
  }
  return { n1000, n500, n250 };
}

/**
 * Berechnet Klemmband-Stückzahl:
 * Summe aller Rohre + Standard-Komponenten (DFR, Russtopf, RE, T-Stück) − 1.
 * Gilt nicht bei 19mm Keramikisolation.
 */
function calcKlemmband(s) {
  if (s.isolation === '19mm') return 0;

  const pipes = calcPipes(s.kaminhoehe, false);
  if (!pipes) return 0;

  let total = pipes.n1000 + pipes.n500 + pipes.n250; // alle Rohrsegmente

  if (s.doppelfutterrohr)  total += 1; // DFR lang (Standard)
  if (s.russtopf)          total += 1; // Russtopf (Standard)
  if (s.reinigungselement) total += 1; // RE Standard
  if (s.tstueck)           total += 1; // T-Stück 90° Standard

  return Math.max(0, total - 1); // N Elemente → N-1 Verbindungen
}

// ══════════════════════════════════════════════════════════════════
// E2. VERSATZ — eine Funktion für Website, Shop-Konfigurator und Tool
// Grundwerte x₀/z₀ (L = 0) je System × Bogenwinkel × Ø. Quellen:
//   ew/dw   Airtec-Katalog 03/2026 S. 25 (SP) / S. 49 (DP), 1:1 aus dem Website-
//           Rechner (Website Airtec Energie/src/components/VersatzCalculator.astro),
//           dort maschinell gegen den Katalog geprüft (0 Abweichungen)
//   steel   Airtec-Preisliste «AIRNOX STEEL — Déviations» (Daniel 17.09.2026), Ø130–250
//   pellet  Airtec-Preisliste «AIRNOX PELLET — Déviations» (Daniel 17.09.2026), Ø80/100
// Formel (alle Systeme, Katalog-Fussnote «raccord c = 58 mm»):
//   x = x₀ + (L − 58)·sin w · z = z₀ + (L − 58)·cos w   (L = Zwischenrohr-Baulänge, 0 = keins)
// ══════════════════════════════════════════════════════════════════

/** Steckmass je Verbindung (mm) — wirksame Länge = Baulänge − Steckmass */
const VERSATZ_STECKMASS = 58;

const VERSATZ_BASE = Object.freeze({
  ew: {
    15: { 80: [38.44, 292.02], 100: [39.12, 297.17], 130: [40.15, 304.95], 150: [40.83, 310.11], 180: [41.85, 317.89], 200: [42.53, 323.08], 250: [44.24, 336.02], 300: [45.94, 348.95], 350: [47.64, 361.89] },
    30: { 80: [79.72, 297.62], 100: [82.40, 307.52], 130: [86.42, 322.52], 150: [89.10, 332.53], 180: [93.12, 347.53], 200: [95.79, 357.49], 250: [102.49, 382.50], 300: [109.19, 407.50], 350: [115.89, 432.51] },
    45: { 80: [121.01, 292.15], 100: [126.87, 306.29], 130: [135.65, 327.49], 150: [141.52, 341.66], 180: [150.30, 362.86], 200: [156.16, 377.00], 250: [170.81, 412.37], 300: [185.45, 447.71], 350: [200.10, 483.08] },
  },
  dw: {
    15: { 80: [45.83, 348.13], 100: [46.51, 353.32], 130: [47.54, 361.10], 150: [48.22, 366.25], 180: [49.24, 374.04], 200: [49.92, 379.19], 250: [51.63, 392.16], 300: [53.33, 405.10], 350: [55.04, 418.03] },
    30: { 80: [98.43, 367.35], 100: [101.11, 377.35], 130: [105.13, 392.35], 150: [107.80, 402.32], 180: [111.82, 417.32], 200: [114.50, 427.32], 250: [121.20, 452.32], 300: [127.90, 477.33], 350: [134.60, 502.33] },
    45: { 80: [154.19, 372.25], 100: [160.05, 386.39], 130: [168.84, 407.62], 150: [174.70, 421.76], 180: [183.48, 442.96], 200: [189.33, 457.09], 250: [203.99, 492.47], 300: [218.62, 527.80], 350: [233.27, 563.17] },
  },
  steel: {
    15: { 130: [40.15, 304.95], 150: [40.83, 310.11], 180: [41.85, 317.89], 200: [42.53, 323.08], 250: [44.24, 336.02] },
    30: { 130: [86.42, 322.52], 150: [89.10, 332.53], 180: [93.12, 347.53], 200: [95.79, 357.49], 250: [102.49, 382.50] },
    45: { 130: [135.65, 327.49], 150: [141.52, 341.66], 180: [150.30, 362.86], 200: [156.16, 377.00], 250: [170.81, 412.37] },
  },
  pellet: {
    15: { 80: [28.09, 213.38], 100: [28.77, 218.53] },
    30: { 80: [59.72, 222.88], 100: [62.40, 232.88] },
    45: { 80: [92.73, 223.87], 100: [98.58, 238.00] },
  },
});

/**
 * Versatz vorwärts: Achsversatz x und Höhenbedarf z für zwei Bögen (2 × w) mit
 * Zwischenrohr der Baulänge L (0 = ohne). Liefert null bei unbekanntem System/Ø/Winkel
 * oder unbrauchbarer Länge (0 < L ≤ Steckmass).
 * @returns {{ x: number, z: number, eff: number } | null}
 */
function versatz({ system, d, winkel, L = 0 }) {
  const base = VERSATZ_BASE[system]?.[winkel]?.[Number(d)];
  if (!base) return null;
  const len = Number(L) || 0;
  if (len < 0 || (len > 0 && len <= VERSATZ_STECKMASS)) return null;
  const eff = len > 0 ? len - VERSATZ_STECKMASS : 0;
  const rad = (Number(winkel) * Math.PI) / 180;
  return { x: base[0] + eff * Math.sin(rad), z: base[1] + eff * Math.cos(rad), eff };
}

/**
 * Versatz rückwärts: gewünschter Achsversatz x → nötige Zwischenrohr-Baulänge L
 * (aufgerundet auf ganze mm) und resultierendes z. `erreichbar` = false, wenn x unter
 * dem Grundversatz x₀ liegt (dann L = 0 und der Mindestversatz wird zurückgegeben).
 * @returns {{ L: number, x: number, z: number, xMin: number, erreichbar: boolean } | null}
 */
function versatzRueckwaerts({ system, d, winkel, x }) {
  const base = VERSATZ_BASE[system]?.[winkel]?.[Number(d)];
  if (!base) return null;
  const rad = (Number(winkel) * Math.PI) / 180;
  const wanted = Number(x);
  if (!(wanted > base[0])) {
    return { L: 0, x: base[0], z: base[1], xMin: base[0], erreichbar: !(wanted > 0) || wanted >= base[0] };
  }
  const eff = (wanted - base[0]) / Math.sin(rad);
  const L = Math.ceil(eff + VERSATZ_STECKMASS);
  return { L, x: wanted, z: base[1] + eff * Math.cos(rad), xMin: base[0], erreichbar: true };
}

// ══════════════════════════════════════════════════════════════════
// E2. STÜCKELUNG — verallgemeinert aus calcPipes für beliebige Nennlängen
// (Ofenrohre: 1000/750/500/330/250/150 mit Steckmass 58; calcPipes selbst bleibt
// unverändert, weil seine Sonderregeln Teil der bewiesenen Stückliste sind).
// Ziel (Parameter `ziel`): 'teile' = so wenige Teile wie möglich, bei gleicher
// Teilezahl der kleinste Überstand (Default — weniger Verbindungen);
// 'ueberstand' = der kleinste Überstand mit höchstens EINEM Teil mehr als nötig
// (ohne diese Grenze fände die Suche exakte Deckungen aus zehn Kurzstücken).
// Bei Gleichstand gewinnen längere Teile. Deterministisch, ohne Zufall.
// Welches Ziel für Ofenrohre gilt, ist Fachentscheid Daniel (Plan §7, Frage 12).
// ══════════════════════════════════════════════════════════════════

/**
 * @param {number} laengeMm            zu deckende Länge (Achsmass) in mm
 * @param {number[]} nennlaengen       verfügbare Baulängen in mm
 * @param {number} steckmass           Steckmass je Verbindung in mm
 * @param {'teile'|'ueberstand'} ziel  Optimierungsziel (siehe oben)
 * @returns {{ teile: Record<number, number>, gesamt: number, ueberstand: number, anzahl: number } | null}
 */
function stueckelung(laengeMm, nennlaengen, steckmass = VERSATZ_STECKMASS, ziel = 'teile') {
  const L = Math.round(Number(laengeMm));
  if (!(L > 0) || !Array.isArray(nennlaengen) || !nennlaengen.length) return null;
  const sizes = [...new Set(nennlaengen.map(Number))].filter((n) => n - steckmass > 0).sort((a, b) => b - a);
  if (!sizes.length) return null;
  const eff = sizes.map((n) => n - steckmass);
  const maxEff = eff[0];
  const limit = L + maxEff;

  // dp[len] = { count, pick } — minimale Teilezahl, die genau len wirksame mm ergibt
  const dp = new Array(limit + 1).fill(null);
  dp[0] = { count: 0, pick: -1 };
  for (let len = 1; len <= limit; len++) {
    for (let i = 0; i < eff.length; i++) {
      const prev = len - eff[i];
      if (prev < 0 || !dp[prev]) continue;
      const cand = dp[prev].count + 1;
      if (!dp[len] || cand < dp[len].count) dp[len] = { count: cand, pick: i };
    }
  }

  let best = -1;
  for (let len = L; len <= limit; len++) {
    if (!dp[len]) continue;
    if (best < 0 || dp[len].count < dp[best].count) best = len; // weniger Teile, sonst kleinerer Überstand (frühere len)
  }
  if (best < 0) return null;
  if (ziel === 'ueberstand') {
    const cap = dp[best].count + 1;
    for (let len = L; len < best; len++) {
      if (dp[len] && dp[len].count <= cap) { best = len; break; }
    }
  }

  const teile = {};
  for (let len = best; len > 0; len -= eff[dp[len].pick]) {
    const n = sizes[dp[len].pick];
    teile[n] = (teile[n] || 0) + 1;
  }
  return { teile, gesamt: best, ueberstand: best - L, anzahl: dp[best].count };
}

// ══════════════════════════════════════════════════════════════════
// E3. BETONIERUNG — reine Funktionen (Aussen-Ø, Volumen, Säcke, Neigung)
// ══════════════════════════════════════════════════════════════════

/** Säcke je m³ (Daniel 17.09.: ~47; 25 l/Sack = 40 rechnerisch + Verdichtung/Verlust) */
const BETON_SAECKE_PRO_M3 = 47;

/** Aussen-Ø des Doppelfutterrohrs = System-Ø + Zuschlag. EW +4 = ANNAHME (Daniel prüft),
 *  DW nach Fachregel Nach Mass: +65 (32.5) / +100 (50) / +115 (57.5) */
const DFR_AUSSEN_ZUSCHLAG = { ew: 4, dw325: 65, dw50: 100, dw575: 115 };

function dfrAussenDurchmesser(d, ausfuehrung) {
  const z = DFR_AUSSEN_ZUSCHLAG[ausfuehrung];
  const dd = Number(d);
  if (!(dd > 0) || z === undefined) return null;
  return dd + z;
}

/**
 * Betonvolumen in m³: Deckenblock b·d·h abzüglich des Rohrzylinders (Aussen-Ø) je Betonierung.
 * Alle Masse in mm. null, wenn ein Mass fehlt oder der Zylinder nicht in den Block passt.
 */
function calcBetonVolumen({ h, b, d, dAussen, anzahl = 1 }) {
  const H = Number(h), B = Number(b), D = Number(d), A = Number(dAussen), n = Math.max(1, Math.round(Number(anzahl) || 1));
  if (!(H > 0) || !(B > 0) || !(D > 0) || !(A > 0)) return null;
  if (A > B || A > D) return null;
  const block = B * D * H;
  const rohr = Math.PI / 4 * A * A * H;
  return n * (block - rohr) / 1e9;
}

function calcBetonSaecke(volumenM3, proM3 = BETON_SAECKE_PRO_M3) {
  const v = Number(volumenM3);
  if (!(v > 0)) return 0;
  return Math.ceil(v * proM3);
}

function gradZuProzent(grad) {
  const g = Number(grad);
  if (!(g >= 0) || g >= 90) return null;
  return Math.tan(g * Math.PI / 180) * 100;
}

function prozentZuGrad(prozent) {
  const p = Number(prozent);
  if (!(p >= 0)) return null;
  return Math.atan(p / 100) * 180 / Math.PI;
}

/**
 * Werkstatt-Tabelle «Umrechnung Meterwinkel in cm → Winkelgrad = Neigungswinkel Dachschräge» (Daniel 18.09.2026,
 * Foto; abgetippt in airtec-b2b-theme/docs/konfigurator-skizzen/umrechnung-meterwinkel-cm-grad.csv).
 * 85 Stützstellen, 68.6 cm … 77.0 cm in 0.1-Schritten. Dieselbe Tabelle steckt im Theme-Snippet b2b-kundenmasse.
 */
const METERWINKEL_CM0 = 68.6;
const METERWINKEL_GRAD = [2.94,3.35,3.77,4.19,4.61,5.04,5.46,5.89,6.32,6.75,7.18,7.61,8.05,8.49,8.93,9.37,9.81,10.26,10.71,11.16,11.61,12.06,12.52,12.98,13.44,13.9,14.37,14.84,15.31,15.78,16.26,16.74,17.22,17.7,18.19,18.68,19.17,19.67,20.17,20.67,21.18,21.69,22.2,22.71,23.23,23.75,24.28,24.81,25.34,25.88,26.42,26.97,27.52,28.07,28.63,29.2,29.77,30.34,30.92,31.5,32.09,32.68,33.28,33.89,34.5,35.12,35.75,36.38,37.02,37.66,38.32,38.98,39.65,40.32,41.01,41.71,42.41,43.13,43.85,44.59,45.34,46.1,46.87,47.66,48.46];

/** cm am Gliedermeter → Grad, linear zwischen den Stützstellen; null ausserhalb 68.6–77.0 */
function meterwinkelCmZuGrad(cm) {
  const c = Number(cm);
  if (!Number.isFinite(c)) return null;
  let pos = Math.round((c - METERWINKEL_CM0) / 0.1 * 1e6) / 1e6;   // Gleitkomma-Rest glätten: 72.5 cm ist exakt Zeile 39
  const last = METERWINKEL_GRAD.length - 1;
  if (pos < -1e-6 || pos > last + 1e-6) return null;
  pos = Math.max(0, Math.min(last, pos));
  const i = Math.floor(pos), f = pos - i;
  return i >= last ? METERWINKEL_GRAD[last] : METERWINKEL_GRAD[i] + (METERWINKEL_GRAD[i + 1] - METERWINKEL_GRAD[i]) * f;
}

const BETON_NEIGUNG_MIN = 1;
const BETON_NEIGUNG_MAX = 60;

/** Neigung einer Betonierung in Grad (eine Dezimale) aus Art grad/prozent/cm — null wenn leer oder ausserhalb 1–60° */
function betonNeigungGrad(b) {
  if (!b || b.neigung === null || b.neigung === undefined || b.neigung === '') return null;
  const v = Number(String(b.neigung).replace(',', '.'));
  if (!(v > 0)) return null;
  const g = b.neigungArt === 'prozent' ? prozentZuGrad(v) : b.neigungArt === 'cm' ? meterwinkelCmZuGrad(v) : v;
  if (g === null || !Number.isFinite(g)) return null;
  const r = Math.round(g * 10) / 10;
  return (r >= BETON_NEIGUNG_MIN && r <= BETON_NEIGUNG_MAX) ? r : null;
}

/**
 * Aufgelöste Wahl EINER Betonierung. `ctx.isolation` liefert den System-Vorschlag, solange der Kunde keines gewählt hat.
 * `komplett` = alle Fragen bis zum Artikel beantwortet. Gibt es eine Bauform im System nicht ohne Schleudern, werden sie
 * erzwungen (generische Regel — Stand 18.09. trifft sie kein System mehr).
 */
function betonAuswahl(b, ctx = {}) {
  const e = b || {};
  const form = e.form === 'schraeg' ? 'schraeg' : e.form === 'flach' ? 'flach' : null;          // Decke / Dach
  const dfr = e.dfr === true ? true : e.dfr === false ? false : null;                           // Doppelfutterrohr zum Einbetonieren?
  // Ausführung des Rohrs: flache Decke ⇒ flach; schräges Dach ⇒ Vorschlag schräg, der Kunde kann «flach» wählen
  const dfrForm = form === null ? null : form === 'flach' ? 'flach' : (e.dfrForm === 'flach' ? 'flach' : 'schraeg');
  const dfrFormVorgeschlagen = form === 'schraeg' && e.dfrForm !== 'flach' && e.dfrForm !== 'schraeg';
  const gueltig = (x) => (x && x !== 'ew' && SKU_MAP.dfr_beton[x]) ? x : null;
  const vorschlag = betonSystemVorschlag(ctx && ctx.isolation);
  const ausf = gueltig(e.system) || vorschlag;
  const systemVorgeschlagen = !gueltig(e.system) && !!vorschlag;
  let schleudern = e.schleudern === true ? true : e.schleudern === false ? false : null;
  let schleudernErzwungen = false;
  if (ausf && dfrForm && !SKU_MAP.dfr_beton[ausf][dfrForm].ohne) { schleudern = true; schleudernErzwungen = true; }
  const neigung = form === 'schraeg' ? betonNeigungGrad(e) : null;                              // Neigung von Decke / Dach
  let hoehe = null;
  if (schleudern === true) {
    const h = Math.round(Number(String(e.hoehe ?? '').replace(',', '.')));
    hoehe = (h >= BETON_H_MIN && h <= BETON_H_MAX) ? h : null;
  }
  // Klemmband = Alternative zu den angeschweissten Schleudern: nur wenn der Kunde Schleudern verneint UND das Klemmband bejaht.
  // Optional — eine offene Klemmband-Frage hält den Artikel nicht zurück.
  const klemmband = schleudern === false && e.klemmband === true;
  // Die Neigung braucht nur das schräge Rohr («nach Gradmass»); ein flaches Rohr im schrägen Dach ist ohne sie bestellbar
  const komplett = dfr === true && !!form && !!ausf && schleudern !== null
    && (dfrForm !== 'schraeg' || neigung !== null) && (schleudern !== true || hoehe !== null);
  return { form, dfr, dfrForm, dfrFormVorgeschlagen, ausf, systemVorgeschlagen, schleudern, schleudernErzwungen, klemmband, neigung, hoehe, komplett };
}

/** Betonvolumen EINER Betonierung in m³ — Frage Doppelfutterrohr oder System noch offen ⇒ kein Volumen */
function betonVolumen(b, diameter, ctx = {}) {
  const a = betonAuswahl(b, ctx);
  // mit Doppelfutterrohr: dessen Aussen-Ø (System nötig) · ohne: der System-Ø des Kamins (Annahme)
  const dAussen = a.dfr === false ? (Number(diameter) > 0 ? Number(diameter) : null) : (a.dfr === true && a.ausf) ? dfrAussenDurchmesser(diameter, a.ausf) : null;
  return { dAussen, vol: calcBetonVolumen({ h: b && b.h, b: b && b.b, d: b && b.d, dAussen, anzahl: 1 }) };
}

// ══════════════════════════════════════════════════════════════════
// TEXTE der neuen Abschnitte (DE/FR) — die Locale-Dateien des Themes sind tabu;
// env.t (Shop: window.EW_I18N) hat Vorrang, fehlt ein Key, greift dieses Wörterbuch.
// ══════════════════════════════════════════════════════════════════

const TEXTS = {
  de: {
    beton_reason:            'Decken-/Dachdurchführung · __AUSF__ · __FORM____SCHL__',
    beton_klemmband_reason:  'Verankerung im Beton mit Klemmband (statt Schleudern) · __SYS__',
    beton_sack_reason:       '__V__ m³ Beton · __N__ Säcke à 25 l (__PRO__ Säcke/m³)',
    artikel_folgt:           'Artikel folgt — noch nicht im Shop, bitte separat anfragen',
    beton_kein_durchmesser:  'In dieser Ausführung nicht für Ø __D__ mm erhältlich',
    // Property-Namen identisch zur Produktseite (Theme-Snippet b2b-kundenmasse) — eine Bestellung sieht gleich aus,
    // egal ob aus dem Konfigurator oder von der Produktseite; die bexio-Übernahme braucht nur EIN Format
    prop_neigung:            'Dachneigung (°)',
    prop_schleudern:         'Schleudern H (mm)',
    ausf_ew: 'einwandig', ausf_dw325: 'DW 32.5 mm', ausf_dw50: 'DW 50 mm', ausf_dw575: 'DW 57.5 mm (EI60)',
    form_flach: 'gerade', form_schraeg: 'schräg',
    mit_schleudern: ' · mit Schleudern', ohne_schleudern: ' · ohne Schleudern',
    saecke: 'Säcke',
  },
  fr: {
    beton_reason:            'Passage de dalle / de toiture · __AUSF__ · __FORM____SCHL__',
    beton_klemmband_reason:  'Ancrage dans le béton par bride (au lieu des pattes) · __SYS__',
    beton_sack_reason:       '__V__ m³ de béton · __N__ sacs de 25 l (__PRO__ sacs/m³)',
    artikel_folgt:           'Article à venir — pas encore en boutique, à demander séparément',
    beton_kein_durchmesser:  'Pas disponible en Ø __D__ mm dans cette exécution',
    prop_neigung:            'Inclinaison du toit (°)',
    prop_schleudern:         'Pattes H (mm)',
    ausf_ew: 'simple paroi', ausf_dw325: 'DP 32.5 mm', ausf_dw50: 'DP 50 mm', ausf_dw575: 'DP 57.5 mm (EI60)',
    form_flach: 'droit', form_schraeg: 'incliné',
    mit_schleudern: ' · avec pattes', ohne_schleudern: ' · sans pattes',
    saecke: 'sacs',
  },
};

// ══════════════════════════════════════════════════════════════════
// ENGINE-FABRIK — injiziert die Umgebung (t, locale, getVariant, …)
// und liefert die BOM-Builder mit exakt der Monolith-Semantik.
// ══════════════════════════════════════════════════════════════════

/**
 * @param {object} env
 * @param {(key: string, vars?: object) => string} [env.t]  i18n (Default: Key-Echo wie Shop ohne EW_I18N)
 * @param {'de'|'fr'} [env.locale]                          Default 'de'
 * @param {(sku: string) => object|null} [env.getVariant]   Produktdaten-Lookup (Default: null = kein Index)
 * @param {number} [env.productsLiquidCount]                nur fürs Shop-Warning in validateState
 */
function createEngine(env = {}) {
  // t()-Default reproduziert das Shop-Verhalten mit leerem EW_I18N:
  // str = key, __VAR__-Ersetzung läuft ins Leere → Key-Echo.
  const baseT = env.t || function t(key, vars) {
    let str = key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        str = str.replace(new RegExp(`__${k.toUpperCase()}__`, 'g'), v);
      }
    }
    return str;
  };
  const locale = env.locale || 'de';
  // E3: Keys, die die Shop-Locale nicht kennt, kommen aus TEXTS (DE/FR) — für alle
  // bisherigen Keys liefert env.t weiterhin exakt das alte Ergebnis (Key-Echo bleibt Key-Echo,
  // solange TEXTS den Key nicht führt).
  const t = function (key, vars) {
    const fromEnv = baseT(key, vars);
    if (fromEnv !== key) return fromEnv;
    let str = (TEXTS[locale] && TEXTS[locale][key]) ?? TEXTS.de[key];
    if (str === undefined) return fromEnv;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        str = str.replace(new RegExp(`__${k.toUpperCase()}__`, 'g'), v);
      }
    }
    return str;
  };
  const getVariant = env.getVariant || (() => null);
  const productsLiquidCount = env.productsLiquidCount || 0;

  function roleLabel(role) {
    const labels = (locale === 'fr') ? ROLE_LABELS_FR : ROLE_LABELS_DE;
    return labels[role] || ROLE_LABELS_DE[role] || role;
  }

  // ─── D. VALIDIERUNG (verbatim; window.EW_PRODUCTS_LIQUID_COUNT → env) ───
  function validateState(s) {
    const errors = [], warnings = [], infos = [];

    if (productsLiquidCount > 250) {
      warnings.push({
        field: 'products', level: 'warning',
        message: t('zu_viele_produkte'),
      });
    }

    if (s.anwendung === 'pellets' && s.diameter && s.diameter > 100) {
      errors.push({
        field: 'diameter', level: 'blocking',
        message: t('validate_pellets', { D: s.diameter }),
      });
    }

    if (s.ofenanschluss && s.anwendung !== 'pellets' && s.diameter && s.diameter < 130) {
      warnings.push({
        field: 'ofenanschluss', level: 'warning',
        message: t('validate_ofenanschluss', { D: s.diameter }),
      });
    }

    if (s.isolation === '19mm' && s.wandstaerke === '1.0') {
      infos.push({
        field: 'wandstaerke', level: 'info',
        message: t('validate_keramik'),
      });
    }

    return { errors, warnings, infos };
  }

  // ─── F. BOM-BUILDER (verbatim; skuIndex.getVariant → env.getVariant) ───
  function makeBomLine(overrides) {
    return {
      key:               overrides.key || `${overrides.role}_${overrides.productId}_${overrides.variantId}`,
      role:              overrides.role,
      productId:         overrides.productId || null,
      variantId:         overrides.variantId || null,
      title:             overrides.title || '',
      titleLine2:        overrides.titleLine2 || null,
      sku:               overrides.sku || '',
      calculatedQuantity: overrides.calculatedQuantity ?? 1,
      quantity:          overrides.quantity ?? overrides.calculatedQuantity ?? 1,
      userTouched:       false,
      defaultQuantity:   overrides.defaultQuantity ?? 0,
      reason:            overrides.reason || '',
      countsForSiliconeSeal: overrides.countsForSiliconeSeal || false,
      optional:          overrides.optional || false,
      error:             overrides.error   || null,
      warning:           overrides.warning || null,
      // Line-Item-Properties (E2): nur vorhanden, wenn eine Zeile welche mitbringt —
      // alte Zeilen bleiben byte-identisch (Golden-Harness des Themes)
      ...(overrides.properties ? { properties: overrides.properties } : {}),
    };
  }

  function skuLine(role, sku, qty, reason = '', overrides = {}) {
    const variant = sku ? getVariant(sku) : null;
    const error = !sku ? 'not_configured' : (!variant ? 'no_variant_found' : null);

    return makeBomLine({
      role,
      sku,
      variantId: variant ? variant.id : null,
      productId: variant ? variant.productId : null,
      title: variant ? variant.title : roleLabel(role),
      calculatedQuantity: qty,
      reason,
      error,
      ...overrides
    });
  }

  function buildCoreBom(s) {
    const lines = [];
    if (!s.anwendung || !s.diameter || !s.kaminhoehe) return lines;

    const is19mm = (s.isolation === '19mm');
    const pipes  = calcPipes(s.kaminhoehe, is19mm);
    if (!pipes) return lines;
    const d = Number(s.diameter);

    // ─── 1. ROHRE ───────────────────────────────────────────────
    const ws = parseFloat(s.wandstaerke) >= 1.0 ? '1.0' : '0.6';
    const pipeSlots = is19mm
      ? [
          { role: 'pipe_19mm_1000', qty: pipes.n1000, sku: SKU_MAP.pipe_19mm[1000]?.[d] },
          { role: 'pipe_19mm_500',  qty: pipes.n500,  sku: SKU_MAP.pipe_19mm[500]?.[d] }
        ]
      : [
          { role: 'pipe_1000', qty: pipes.n1000, sku: SKU_MAP.pipe[ws]?.[1000]?.[d] },
          { role: 'pipe_500',  qty: pipes.n500,  sku: SKU_MAP.pipe[ws]?.[500]?.[d] },
          { role: 'pipe_250',  qty: pipes.n250,  sku: SKU_MAP.pipe[ws]?.[250]?.[d] },
        ];

    for (const { role, qty, sku } of pipeSlots) {
      if (qty > 0) {
        lines.push(skuLine(role, sku, qty,
          is19mm ? t('reason_kaminhoehe', { H: s.kaminhoehe }) : t('reason_kaminhoehe_wand', { H: s.kaminhoehe, W: ws }),
          { countsForSiliconeSeal: true, key: `${role}_${d}_${ws.replace('.', '')}` }
        ));
      }
    }

    // ─── 2. ISOLATION (30mm / 50mm) ─────────────────────────────
    if (s.isolation === '30mm' || s.isolation === '50mm') {
      const role = s.isolation === '30mm' ? 'iso_30mm' : 'iso_50mm';
      const isoQty = s.kaminhoehe ? Math.ceil(parseFloat(s.kaminhoehe)) : 1;
      const sku = s.isolation === '30mm' ? SKU_MAP.iso_30mm[d] : SKU_MAP.iso_50mm[d];
      lines.push(skuLine(role, sku, isoQty, t('reason_isolation', { ISO: s.isolation, QTY: isoQty, H: s.kaminhoehe }), { key: `${role}_${d}` }));
    }

    // ─── 2b. ALUMINIUMKLEBEBAND (bei 30mm / 50mm Isolation) ─────
    if (s.isolation === '30mm' || s.isolation === '50mm') {
      lines.push(skuLine('alu_tape_50mm', SKU_MAP.alu_tape_50mm, 1, t('standard_breite_50'), { key: 'alu_tape_50mm' }));
      lines.push(skuLine('alu_tape_100mm', SKU_MAP.alu_tape_100mm, 0, t('alt_breite_100'), { key: 'alu_tape_100mm', optional: true }));
    }

    // ─── 3. SCHÜTTISOLATION (Vermiculite) ───────────────────────
    if (is19mm && s.schuettisolation) {
      lines.push(skuLine('schuetti', SKU_MAP.schuetti, 1, t('schuetti_empfohlen'), { key: 'schuetti' }));
    }

    // ─── 4. DOPPELFUTTERROHR ────────────────────────────────────
    if (s.doppelfutterrohr) {
      lines.push(skuLine('dfr_lang',   SKU_MAP.dfr_lang[d],   1, t('standard_l330'), { countsForSiliconeSeal: true, key: `dfr_lang_${d}` }));
      lines.push(skuLine('dfr_mittel', SKU_MAP.dfr_mittel[d], 0, t('alt_l200'),      { countsForSiliconeSeal: true, key: `dfr_mittel_${d}`, optional: true }));
      lines.push(skuLine('dfr_kurz',   SKU_MAP.dfr_kurz[d],   0, t('alt_l50'),       { countsForSiliconeSeal: true, key: `dfr_kurz_${d}`, optional: true }));
    }

    // ─── 5. RUSSTOPF ────────────────────────────────────────────
    if (s.russtopf) {
      const isKondensat = (s.anwendung === 'oel' || s.anwendung === 'gas');
      if (!isKondensat) {
        lines.push(skuLine('russtopf_geschlossen', SKU_MAP.russtopf_geschlossen[d], 1, t('reason_standard'), { countsForSiliconeSeal: true, key: `russtopf_geschlossen_${d}` }));
        lines.push(skuLine('russtopf_skof',        SKU_MAP.russtopf_skof[d],        0, t('reason_alternative'), { countsForSiliconeSeal: true, key: `russtopf_skof_${d}`, optional: true }));
      } else {
        lines.push(skuLine('russtopf_kondensat_unten',      SKU_MAP.russtopf_kondensat_unten[d],      1, t('reason_standard'), { countsForSiliconeSeal: true, key: `russtopf_kondensat_unten_${d}` }));
        lines.push(skuLine('russtopf_kondensat_seitlich',   SKU_MAP.russtopf_kondensat_seitlich[d],   0, t('reason_alternative'), { countsForSiliconeSeal: true, key: `russtopf_kondensat_seitlich_${d}`, optional: true }));
        lines.push(skuLine('russtopf_skof_kondensat_unten', SKU_MAP.russtopf_skof_kondensat_unten[d], 0, t('reason_alternative'), { countsForSiliconeSeal: true, key: `russtopf_skof_kondensat_unten_${d}`, optional: true }));
        lines.push(skuLine('russtopf_skof_kondensat_seit',  SKU_MAP.russtopf_skof_kondensat_seit[d],  0, t('reason_alternative'), { countsForSiliconeSeal: true, key: `russtopf_skof_kondensat_seit_${d}`, optional: true }));
      }
    }

    // ─── 6. REINIGUNGSELEMENT ───────────────────────────────────
    if (s.reinigungselement) {
      lines.push(skuLine('re_standard', SKU_MAP.re_standard[d], 1, t('reason_standard'),    { countsForSiliconeSeal: true, key: `re_standard_${d}` }));
      lines.push(skuLine('re_alt',      SKU_MAP.re_alt[d],      0, t('reason_alternative'), { key: `re_alt_${d}`, optional: true }));
    }

    // ─── 7. T-STÜCK ─────────────────────────────────────────────
    if (s.tstueck) {
      lines.push(skuLine('tstueck_90',     SKU_MAP.tstueck_90[d],     1, t('reason_standard') + ' (90°)',    { countsForSiliconeSeal: true, key: `tstueck_90_${d}` }));
      lines.push(skuLine('tstueck_87',     SKU_MAP.tstueck_87[d],     0, t('reason_alternative') + ' (87°)', { countsForSiliconeSeal: true, key: `tstueck_87_${d}`, optional: true }));
      lines.push(skuLine('tstueck_60',     SKU_MAP.tstueck_60[d],     0, t('reason_alternative') + ' (60°)', { countsForSiliconeSeal: true, key: `tstueck_60_${d}`, optional: true }));
      lines.push(skuLine('tstueck_45',     SKU_MAP.tstueck_45[d],     0, t('reason_alternative') + ' (45°)', { countsForSiliconeSeal: true, key: `tstueck_45_${d}`, optional: true }));
      lines.push(skuLine('tstueck_90_dfr', SKU_MAP.tstueck_90_dfr[d], 0, t('reason_alternative') + ' (90° DFR)', { countsForSiliconeSeal: true, key: `tstueck_90_dfr_${d}`, optional: true }));
      lines.push(skuLine('tklix_90',       SKU_MAP.tklix_90[d],       0, t('reason_alternative') + ' (T-Klix 90°)', { countsForSiliconeSeal: true, key: `tklix_90_${d}`, optional: true }));
    }

    // ─── KLEMMBAND (nicht bei 19mm Keramik) ─────────────────────
    if (!is19mm) {
      const kbQty = calcKlemmband(s);
      if (kbQty > 0) {
        lines.push(skuLine('klemmband', SKU_MAP.klemmband[d], kbQty,
          '',
          { key: `klemmband_${d}` }
        ));
      }
    }

    // ─── SCHACHTELEMENTE ────────────────────────────────────────
    if (s.leichtbauschacht && (s.isolation === '30mm' || s.isolation === '50mm') && s.diameter && s.kaminhoehe) {
      const innenmassMap = SCHACHT_INNENMASS[s.isolation] || {};
      const innenmass    = innenmassMap[d] || null;

      if (innenmass) {
        const { n1000, n500, n250 } = calcSchacht(s.kaminhoehe);
        const schachtMap = SKU_MAP.schacht;

        if (n1000 > 0) lines.push(skuLine('schacht_1000', schachtMap[1000][s.isolation][innenmass], n1000, t('lbs_reason', { S: '1000mm', I: innenmass, ISO: s.isolation }), { key: `schacht_1000_${d}`, titleLine2: t('lbs_label', { S: '1000mm', I: innenmass }) }));
        if (n500 > 0)  lines.push(skuLine('schacht_500',  schachtMap[500][s.isolation][innenmass],  n500,  t('lbs_reason', { S: '500mm', I: innenmass, ISO: s.isolation }),  { key: `schacht_500_${d}`, titleLine2: t('lbs_label', { S: '500mm', I: innenmass }) }));
        if (n250 > 0)  lines.push(skuLine('schacht_250',  schachtMap[250][s.isolation][innenmass],  n250,  t('lbs_reason', { S: '250mm', I: innenmass, ISO: s.isolation }),  { key: `schacht_250_${d}`, titleLine2: t('lbs_label', { S: '250mm', I: innenmass }) }));
      }
    }

    // ─── 8b. KLEBER FÜR LEICHTBAUSCHACHT ─────────────────────────
    if (s.leichtbauschacht && (s.isolation === '30mm' || s.isolation === '50mm') && s.kaminhoehe) {
      const schachtForKleber = calcSchacht(s.kaminhoehe);
      const totalSchachtElemente = schachtForKleber.n1000 + schachtForKleber.n500 + schachtForKleber.n250;
      const kleberQty = Math.floor(totalSchachtElemente / 2);
      if (kleberQty > 0) {
        lines.push(skuLine('lbs_kleber', SKU_MAP.lbs_kleber, kleberQty,
          '',
          { key: 'lbs_kleber' }
        ));
      }
    }

    // ─── SCHRAUBEN FÜR LEICHTBAUSCHACHT ─────────────────────────
    if (s.leichtbauschacht && (s.isolation === '30mm' || s.isolation === '50mm') && s.kaminhoehe) {
      const schachtForSchrauben = calcSchacht(s.kaminhoehe);
      const totalElemente = schachtForSchrauben.n1000 + schachtForSchrauben.n500 + schachtForSchrauben.n250;
      const schraubenQty = totalElemente * 8;
      if (schraubenQty > 0) {
        lines.push(skuLine('lbs_schrauben', SKU_MAP.lbs_schrauben, schraubenQty,
          t('lbs_schrauben_reason'),
          { key: 'lbs_schrauben' }
        ));
      }
    }

    // ─── 8. SILIKON-DICHTUNG (nur Öl) ───────────────────────────
    if (s.anwendung === 'oel') {
      lines.push(skuLine('silikon_dichtung', SKU_MAP.silikon_dichtung[d], 1, t('oel_empfohlen'), { key: `silikon_dichtung_${d}` }));
    }

    return lines;
  }

  function buildRoofBom(s) {
    const lines = [];
    const d = Number(s.diameter);
    const isFr = locale === 'fr';

    function surfaceLabel(surface) {
      if (!surface) return '—';
      const map = {
        'kupfer':         isFr ? 'cuivre'      : 'Kupfer',
        'inox-matt':      isFr ? 'inox mat'    : 'Inox matt',
        'inox-gebuestet': isFr ? 'inox brossé' : 'Inox gebürstet',
      };
      return map[surface] || surface;
    }
    const KAMINHUT_TYPE_LABEL = { airnox: 'Airnox', 'airnox-turbo': 'Airnox Turbo', sirius: 'Sirius' };
    const surface = s.kaminhutSurface || 'none';

    // ─── Kaminabdeckplatte ──────────────────────────────────────
    if (s.kaminabdeckplatte) {
      const isDW = s.kaminabdeckplatteStutzen === 'doppelwandig';
      const kapType = isDW ? 'dw' : 'ew';
      const role = isDW ? 'kaminabdeckplatte_dw' : 'kaminabdeckplatte';
      const sku = SKU_MAP.kaminabdeckplatte[kapType][surface]?.[d];

      lines.push(skuLine(role, sku, 1,
        isDW ? t('dw_reason', { D: s.diameter, SUR: surfaceLabel(surface) }) : t('ew_reason', { D: s.diameter, SUR: surfaceLabel(surface) }),
        { key: `kaminabdeckplatte_${kapType}_${d}_${surface}` }
      ));
    }

    // ─── Dila rund (Dilatabschluss) ─────────────────────────────
    if (s.dilaRund) {
      const isoKey  = s.isolation === '50mm' ? '50mm' : '32.5mm';
      const surfKey = surface === 'kupfer' ? 'kupfer' : 'inox-matt';
      const role    = `dilatabschluss_${s.isolation === '50mm' ? '50mm' : '32mm'}_${surface === 'kupfer' ? 'kupfer' : 'inox'}`;
      const sku     = SKU_MAP.dilatabschluss[isoKey]?.[surfKey]?.[d];

      lines.push(skuLine(role, sku, 1, t('dila_reason', { IK: isoKey, D: s.diameter }), { key: `dilatabschluss_${isoKey}_${d}_${surfKey}` }));
    }

    // ─── Kaminhut ───────────────────────────────────────────────
    if (s.kaminhut && s.kaminhut !== 'nein') {
      const isSirius = s.kaminhut === 'sirius';
      const role = isSirius ? 'kaminhut_sirius' : 'kaminhut_airnox';
      const sku = SKU_MAP.kaminhut[s.kaminhut]?.[surface]?.[d];

      lines.push(skuLine(role, sku, 1, t('kaminhut_reason', { T: KAMINHUT_TYPE_LABEL[s.kaminhut] || s.kaminhut, SUR: surfaceLabel(surface) }), { key: `kaminhut_${s.kaminhut}_${surface}` }));
    }

    return lines;
  }

  // ─── E2: Abschnitt 3 «Decken-/Dachdurchführung (Betonierung)» ───────
  // Fachlogik folgt in Etappe E3. Ohne Hauptschalter `betonierung` liefert der
  // Builder nichts — Invariante «Default neu = alt» (Golden-Harness des Themes).
  function buildMountingBom(s) {
    if (!s || s.betonierung !== true) return [];
    if (!s.anwendung || !s.diameter) return [];
    const liste = Array.isArray(s.betonierungen) ? s.betonierungen : [];
    if (!liste.length) return [];
    const d = Number(s.diameter);
    const dd = String(d).padStart(3, '0');

    // Gleiche Artikel mit gleichen Kundenmassen werden zu EINER Zeile mit Menge n zusammengefasst
    // (Shopify führt gleiche Variante + gleiche Properties ohnehin zusammen).
    const dfrGruppen = new Map();
    const kbGruppen = new Map();
    let volSumme = 0;
    const konfig = [];

    liste.forEach((b, idx) => {
      const a = betonAuswahl(b, s);
      const { dAussen, vol } = betonVolumen(b, d, s);
      if (vol) volSumme += vol;
      konfig.push({
        nr: idx + 1, form: a.form, dfr: a.dfr, dfrForm: a.dfrForm, ausf: a.ausf, schleudern: a.schleudern, klemmband: a.klemmband, hoehe: a.hoehe, neigungGrad: a.neigung,
        masse: vol ? { h: Number(b.h), b: Number(b.b), t: Number(b.d), dAussen } : null,
        volumenM3: vol ? Math.round(vol * 1000) / 1000 : null,
      });
      if (!a.komplett) return;

      const base = SKU_MAP.dfr_beton[a.ausf][a.dfrForm][a.schleudern ? 'mit' : 'ohne'];
      const nProp = a.dfrForm === 'schraeg' ? a.neigung : null;   // Neigung nur am schrägen Rohr
      const dfrOk = DFR_BETON_DIAMETERS[a.ausf].includes(d);
      const sku = (base && dfrOk) ? `${base}.${dd}` : '';
      const props = {};
      if (nProp !== null) props[t('prop_neigung')] = nProp.toFixed(1);
      if (a.hoehe !== null) props[t('prop_schleudern')] = String(a.hoehe);
      const key = `dfr_beton_${a.ausf}_${a.dfrForm}_${a.schleudern ? 'mit' : 'ohne'}_${d}_n${nProp !== null ? nProp.toFixed(1) : '-'}_h${a.hoehe !== null ? a.hoehe : '-'}`;
      const g = dfrGruppen.get(key) || { a, sku, dfrOk, props, nrn: [] };
      g.nrn.push(idx + 1);
      dfrGruppen.set(key, g);

      if (a.klemmband) {
        const kbKey = `klemmband_beton_${a.ausf}_${d}`;
        const k = kbGruppen.get(kbKey) || { ausf: a.ausf, n: 0 };
        k.n += 1;
        kbGruppen.set(kbKey, k);
      }
    });

    const saecke = volSumme > 0 ? calcBetonSaecke(volSumme) : 0;
    const konfigJson = JSON.stringify({
      v: 2, abschnitt: 'betonierung', d, betonierungen: konfig,
      volumenM3: volSumme > 0 ? Math.round(volSumme * 1000) / 1000 : null, saecke,
    });
    const lines = [];

    // 1. Deckendurchlauf-Doppelfutterrohre
    for (const [key, g] of dfrGruppen) {
      const ausfLabel = t(`ausf_${g.a.ausf}`);
      const schlLabel = t(g.a.schleudern ? 'mit_schleudern' : 'ohne_schleudern');
      lines.push(skuLine('dfr_beton', g.sku, g.nrn.length,
        t('beton_reason', { AUSF: ausfLabel, FORM: t(`form_${g.a.dfrForm}`), SCHL: schlLabel }),
        {
          key,
          properties: { ...g.props, _ew_konfig: konfigJson },
          ...(g.dfrOk ? {} : { error: null, warning: t('beton_kein_durchmesser', { D: d }) }),
        }));
    }

    // 2. Klemmband des Systems — nur wo der Kunde es statt der Schleudern als Verankerung gewählt hat
    for (const [key, k] of kbGruppen) {
      const kbSku = `${SKU_MAP.klemmband_system[k.ausf]}.${dd}`;
      const kbVariant = getVariant(kbSku);
      lines.push(skuLine('klemmband_beton', kbSku, k.n,
        t('beton_klemmband_reason', { SYS: t(`ausf_${k.ausf}`) }),
        { key, ...(kbVariant ? {} : { error: null, warning: t('artikel_folgt') }) }));
    }

    // 3. Beton-Säcke — Summe aller Betonierungen mit vollständigen Massen, einmal aufgerundet
    if (saecke > 0) {
      const sackVariant = getVariant(SKU_MAP.beton_sack);
      lines.push(skuLine('beton_sack', SKU_MAP.beton_sack, saecke,
        t('beton_sack_reason', { V: volSumme.toFixed(3), N: saecke, PRO: BETON_SAECKE_PRO_M3 }),
        { key: 'beton_sack', ...(sackVariant ? {} : { error: null, warning: t('artikel_folgt') }) }));
    }

    return lines;
  }

  // ─── E2: Abschnitt 5 «Ofenanschluss» ────────────────────────────────
  // Fachlogik folgt in Etappe E5. `ofenanschluss` existiert seit jeher im State
  // (Warnregel in validateState), bekam aber nie eine Stückliste — bis E5 bleibt
  // das so, damit bestehende Zustände identisch rechnen.
  function buildOfenanschlussBom(s) {
    if (!s || s.ofenanschluss !== true) return [];
    return [];
  }

  /** Komplette Materialliste: Core + Dach + Durchführung (E3) + Ofenanschluss (E5) */
  function buildBom(s) {
    return [
      ...buildCoreBom(s),
      ...buildRoofBom(s),
      ...buildMountingBom(s),
      ...buildOfenanschlussBom(s),
    ];
  }

  // makeBomLine ist nach aussen sichtbar, weil der Shop-Adapter (manuelle Suche)
  // damit Zeilen anlegt — Stufe 2 S1: das Theme-Asset wird aus dieser Engine gebaut.
  return {
    validateState, buildCoreBom, buildRoofBom, buildMountingBom, buildOfenanschlussBom,
    buildBom, roleLabel, makeBomLine,
  };
}

// ══════════════════════════════════════════════════════════════════
// B. SKU INDEX
// ══════════════════════════════════════════════════════════════════

const skuIndex = {
  bySku: {}, // '101-0106-1001-10.080' -> { id, productId, title, price, ... }
  loaded: false,

  init() {
    let rawProducts = [];
    
    const el = document.getElementById('EwConfiguratorProducts');
    if (el) {
      try { rawProducts = rawProducts.concat(JSON.parse(el.textContent)); } catch(e) {}
    }
    
    const extraEl = document.getElementById('EwExtraProducts');
    if (extraEl) {
      try { rawProducts = rawProducts.concat(JSON.parse(extraEl.textContent).filter(Boolean)); } catch(e) {}
    }

    for (const item of rawProducts) {
      if (item && item.sku) {
        this.bySku[item.sku] = item;
      }
    }

    this.loaded = true;

    if ((window.EW_PRODUCTS_LIQUID_COUNT || 0) > 250) {
      console.warn('[EW] Collection enthält mehr als 250 Produkte — der serverseitige Produktindex ist unvollständig (Liquid paginate-Limit).');
    }

    document.dispatchEvent(new CustomEvent('ew:products-ready', { detail: { count: Object.keys(this.bySku).length } }));
  },

  /**
   * Lädt Extra-Produkte nach, die das Liquid all_products-Limit (20 Handles
   * pro Seite) überschreiten — via öffentlicher /products/<handle>.js API.
   */
  async fetchExtraProducts() {
    const handles = window.EW_FETCH_HANDLES || [];
    if (!handles.length) return;
    // routes.root_url liefert '/' bzw. '/fr' (ohne Schluss-Slash) → normalisieren
    const root = (window.EW_ROOT_URL || '/').replace(/\/?$/, '/');

    await Promise.all(handles.map(async (handle) => {
      try {
        const res = await fetch(`${root}products/${handle}.js`);
        if (!res.ok) { console.warn('[EW] Extra-Produkt nicht gefunden:', handle); return; }
        const p = await res.json();
        for (const v of p.variants || []) {
          if (!v.sku) continue;
          const img = p.featured_image
            ? p.featured_image + (p.featured_image.includes('?') ? '&width=200' : '?width=200')
            : null;
          this.bySku[v.sku] = {
            sku: v.sku,
            id: v.id,
            productId: p.id,
            title: (v.title && v.title !== 'Default Title') ? `${p.title} - ${v.title}` : p.title,
            price: v.price,
            available: v.available,
            url: `${root}products/${handle}`,
            image: img,
          };
        }
      } catch (e) {
        console.warn('[EW] Extra-Produkt konnte nicht geladen werden:', handle, e);
      }
    }));
  },

  getVariant(sku) {
    if (!sku) return null;
    return this.bySku[sku] || null;
  },

  search(q) {
    if (!q || q.length < 2) return [];
    const ql = q.toLowerCase();
    const results = [];
    for (const [sku, variant] of Object.entries(this.bySku)) {
      if (variant.title.toLowerCase().includes(ql) || sku.toLowerCase().includes(ql)) {
        results.push({ variant });
        if (results.length >= 20) return results;
      }
    }
    return results;
  }
};

// ══════════════════════════════════════════════════════════════════
// SHOP-GLUE — bindet den Rechenkern (createEngine aus engine/ew-engine.mjs)
// an die Shop-Umgebung und stellt genau die Top-Level-Namen bereit, die der
// UI-Adapter (30-ui.js), der Golden-Harness des Themes
// (dev/tests/ew-konfigurator) und bin/live-parity.mjs erwarten:
//   state · buildBom() · validateState(s) · makeBomLine(o) · roleLabel(role)
// Die Nähte entsprechen 1:1 den in engine/UPSTREAM.md dokumentierten
// Abweichungen (t → env.t, EW_LOCALE → env.locale, skuIndex → env.getVariant,
// EW_PRODUCTS_LIQUID_COUNT → env.productsLiquidCount).
// ══════════════════════════════════════════════════════════════════

const ROLE_LABELS = ROLE_LABELS_DE; // ungenutzter Alias des Monolithen — bewusst erhalten

const __ewEngine = createEngine({
  t,
  locale: window.EW_LOCALE,
  getVariant: (sku) => skuIndex.getVariant(sku),
  productsLiquidCount: window.EW_PRODUCTS_LIQUID_COUNT,
});

/** Gibt den lesbaren Label für eine Rolle zurück — locale-aware (Fallback: Rolle selbst) */
function roleLabel(role) {
  return __ewEngine.roleLabel(role);
}

// ══════════════════════════════════════════════════════════════════
// C. STATE — Single Source of Truth
//    Formularwerte aus createDefaultState() (eine Quelle für Shop und Tool),
//    Laufzeit-Listen leer. resetAll() im UI-Adapter setzt dieselben Werte.
// ══════════════════════════════════════════════════════════════════

const state = Object.assign(createDefaultState(), { bom: [], errors: [], warnings: [], infos: [] });

// ══════════════════════════════════════════════════════════════════
// D. VALIDIERUNG / F. BOM-BUILDER — Delegation an den Kern
// ══════════════════════════════════════════════════════════════════

function validateState(s) {
  return __ewEngine.validateState(s);
}

/** Erzeugt eine strukturierte BOM-Zeile mit korrekten Defaults */
function makeBomLine(overrides) {
  return __ewEngine.makeBomLine(overrides);
}

function buildBom() {
  return __ewEngine.buildBom(state);
}

// ══════════════════════════════════════════════════════════════════
// E2. ABSCHNITTS-MODULE + SICHTBARKEITS-REGELN (Shop-Adapter)
//
// Neue Abschnitte (E3 Betonierung, E5 Ofenanschluss) hängen sich hier ein,
// statt updateFormDisplay() weiter wachsen zu lassen. Vertrag je Modul:
//   { id:        'betonierung',                    ↔ <div data-ew-module="betonierung"><div class="ew-module-host"></div></div>
//     questions: (locale) => HTML-String,          Fragen mit .ew-option-btn[data-key][data-value] / .ew-input[data-key]
//     sync:      (state, root) => void,            Skizzen, Rechenwerte, abhängige Anzeigen nachführen
//     bind?:     (root) => void }                  einmalig nach dem Rendern (optionale Zusatz-Events)
// Boot: renderSectionModules() VOR initForm() — so greifen die bestehende
// Button-Bindung und die Aktiv-Markierung automatisch. recompute()/resetAll()
// rufen syncSectionModules() nach updateFormDisplay().
//
// Sichtbarkeit: data-show-if / data-show-unless tragen JSON-Regeln. Bestehende
// Form {key, eq} bleibt gültig (String-Vergleich wie bisher); neu:
//   {key, neq}  {key, in:[…]}  {key, gte}  {key, lte}  {key, truthy}
//   {all:[…]}   {any:[…]}      {not:{…}}
// ══════════════════════════════════════════════════════════════════

const SECTION_MODULES = [];

function registerSectionModule(mod) {
  if (!mod || !mod.id) return;
  const i = SECTION_MODULES.findIndex(m => m.id === mod.id);
  if (i >= 0) SECTION_MODULES[i] = mod; else SECTION_MODULES.push(mod);
}

function sectionModuleRoot(id) {
  return document.querySelector(`[data-ew-module="${id}"]`);
}

/** Rendert die Fragen aller Module in ihre Liquid-Container (einmal beim Boot) */
function renderSectionModules() {
  const locale = (window.EW_LOCALE === 'fr') ? 'fr' : 'de';
  for (const mod of SECTION_MODULES) {
    const root = sectionModuleRoot(mod.id);
    if (!root) continue;
    const host = root.querySelector('.ew-module-host') || root;
    if (typeof mod.questions === 'function') host.innerHTML = mod.questions(locale);
    if (typeof mod.bind === 'function') mod.bind(root);
  }
}

/** Führt Skizzen/Anzeigen aller Module nach — nach jedem updateFormDisplay() */
function syncSectionModules(s) {
  for (const mod of SECTION_MODULES) {
    if (typeof mod.sync !== 'function') continue;
    const root = sectionModuleRoot(mod.id);
    if (root) mod.sync(s, root);
  }
}

/** Wertet eine Sichtbarkeits-Regel gegen den State aus (siehe Kopf) */
function evalCond(cond, s) {
  if (!cond || typeof cond !== 'object') return false;
  if (Array.isArray(cond.all)) return cond.all.every(c => evalCond(c, s));
  if (Array.isArray(cond.any)) return cond.any.some(c => evalCond(c, s));
  if (cond.not) return !evalCond(cond.not, s);
  const v = s[cond.key];
  if ('eq' in cond)     return String(v) === String(cond.eq);
  if ('neq' in cond)    return String(v) !== String(cond.neq);
  if ('in' in cond)     return Array.isArray(cond.in) && cond.in.some(x => String(x) === String(v));
  if ('gte' in cond)    return Number(v) >= Number(cond.gte);
  if ('lte' in cond)    return Number(v) <= Number(cond.lte);
  if ('truthy' in cond) return !!v === !!cond.truthy;
  return false;
}

// ══════════════════════════════════════════════════════════════════
// G. RENDERING (Sprint 2/3 füllt dies aus)
// ══════════════════════════════════════════════════════════════════

/** Übersetzt interne BOM-Fehlercodes in kundenfreundliche Texte */
function bomErrorLabel(code) {
  if (code === 'not_configured')   return t('keine_variante', { D: state.diameter || '—' });
  if (code === 'no_variant_found') return t('kein_produkt');
  return code;
}

/** Rendert die BOM-Tabelle */
function renderBomTable(bom) {
  const tbody = document.getElementById('ew-bom-tbody');
  if (!tbody) return;

  if (!bom.length) {
    tbody.innerHTML = `
      <tr class="ew-bom__empty-row">
        <td colspan="6">
          <span class="ew-bom__empty-hint">${t('bom_leer')}</span>
        </td>
      </tr>`;
    return;
  }

  // Sprint 2/3: vollständiges Rendering kommt hier
  tbody.innerHTML = bom.map((line, i) => {
    const variant = skuIndex.getVariant(line.sku);
    const img  = variant?.image;
    const imgCell = img
      ? `<td class="ew-bom-col--img"><img src="${esc(img)}" alt="" width="52" height="52" loading="lazy" style="object-fit:contain;border-radius:4px;"></td>`
      : `<td class="ew-bom-col--img"><span class="ew-bom-img--placeholder"></span></td>`;
    return `
    <tr class="${line.optional && line.quantity === 0 ? 'ew-bom-row--optional' : ''}">
      <td class="ew-bom-col--pos">${i + 1}</td>
      ${imgCell}
      <td>
        <strong>${esc(line.title)}</strong>
        ${line.reason  ? `<br><span class="ew-bom__reason">${esc(line.reason)}</span>` : ''}
        ${line.warning ? `<br><span style="color:#e67e22;font-size:1.1rem;font-style:italic">⚠ ${esc(line.warning)}</span>` : ''}
        ${line.error   ? `<br><span style="color:#d9534f;font-size:1.1rem">⚠ ${esc(bomErrorLabel(line.error))}</span>` : ''}
      </td>
      <td class="b2b-sku-cell">${esc(line.sku) || '—'}</td>
      <td class="ew-bom-col--qty">
        <div class="b2b-qty-stepper">
          <button class="b2b-qty-btn" data-bom-key="${esc(line.key)}" data-action="dec" aria-label="${window.EW_LOCALE === 'fr' ? 'Moins' : 'Weniger'}">−</button>
          <input class="b2b-qty-input" type="number" min="0" value="${line.quantity}"
            data-bom-key="${esc(line.key)}" aria-label="${window.EW_LOCALE === 'fr' ? 'Quantité' : 'Menge'}">
          <button class="b2b-qty-btn" data-bom-key="${esc(line.key)}" data-action="inc" aria-label="${window.EW_LOCALE === 'fr' ? 'Plus' : 'Mehr'}">+</button>
        </div>
      </td>
      <td class="ew-bom-col--cart">
        ${line.variantId
          ? `<button class="b2b-add-to-cart-btn" data-variant-id="${line.variantId}" data-key="${esc(line.key)}" aria-label="${window.EW_LOCALE === 'fr' ? 'Ajouter au panier' : 'In Warenkorb'}">
               <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
             </button>`
          : '<span style="color:#ccc">—</span>'
        }
      </td>
    </tr>
  `;}).join('');

  // Qty-Stepper Events binden
  tbody.querySelectorAll('[data-bom-key]').forEach(el => {
    el.addEventListener('click', e => {
      const key = e.target.dataset.bomKey;
      const action = e.target.dataset.action;
      if (!action) return;
      const line = state.bom.find(l => l.key === key);
      if (!line) return;
      if (action === 'inc') line.quantity = Math.max(0, line.quantity + 1);
      if (action === 'dec') line.quantity = Math.max(0, line.quantity - 1);
      line.userTouched = true;
      renderBomTable(state.bom);
      renderSummary();
    });
    if (el.tagName === 'INPUT') {
      el.addEventListener('change', e => {
        const key = e.target.dataset.bomKey;
        const line = state.bom.find(l => l.key === key);
        if (!line) return;
        line.quantity = Math.max(0, parseInt(e.target.value, 10) || 0);
        line.userTouched = true;
        renderSummary();
      });
    }
  });

  // Einzel-Cart-Buttons — Menge live aus dem State lesen, damit auch per
  // Tastatur getippte Mengen (ohne Tabellen-Re-Render) korrekt übernommen werden
  tbody.querySelectorAll('.b2b-add-to-cart-btn[data-variant-id]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const variantId = parseInt(btn.dataset.variantId, 10);
      const line      = state.bom.find(l => l.key === btn.dataset.key);
      const qty       = Math.max(1, line ? line.quantity : 1);
      await addSingleToCart(variantId, qty, btn, line?.properties || null);
    });
  });
}

/** Rendert Warning/Error-Banners */
function renderAlerts({ errors, warnings, infos }) {
  const container = document.getElementById('ew-alerts');
  if (!container) return;

  const all = [
    ...errors.map(e  => ({ ...e, level: 'blocking' })),
    ...warnings.map(w => ({ ...w, level: 'warning' })),
    ...infos.map(i    => ({ ...i, level: 'info' })),
  ];

  if (!all.length) { container.innerHTML = ''; return; }

  container.innerHTML = all.map(a => `
    <div class="ew-alert ew-alert--${a.level}" role="alert">
      ${a.level === 'blocking' ? '🚫' : a.level === 'warning' ? '⚠️' : 'ℹ️'}
      ${a.message}
    </div>
  `).join('');
}

/** Aktualisiert die Sticky Summary Bar */
function renderSummary() {
  const summaryText = document.getElementById('ew-summary-text');
  const summaryBom  = document.getElementById('ew-summary-bom');
  if (!summaryText || !summaryBom) return;

  const parts = [];
  if (state.anwendung)               parts.push(state.anwendung.charAt(0).toUpperCase() + state.anwendung.slice(1));
  if (state.diameter)                parts.push(`Ø ${state.diameter} mm`);
  if (state.kaminhoehe)              parts.push(`${state.kaminhoehe} m`);
  if (state.isolation !== 'keine')   parts.push(`Isolation ${state.isolation}`);
  const kaminhutLabel = { airnox: 'Airnox', 'airnox-turbo': 'Airnox Turbo', sirius: 'Sirius' };
  if (state.kaminhut !== 'nein')     parts.push(t('kaminhut_label', { N: kaminhutLabel[state.kaminhut] || state.kaminhut }));

  summaryText.textContent = parts.length ? parts.join(' · ') : t('noch_keine_auswahl');

  const activeCount = state.bom.filter(l => l.quantity > 0).length;
  summaryBom.textContent = activeCount
    ? t('positionen', { N: activeCount })
    : '—';

  const addBtn = document.getElementById('ew-add-to-cart-btn');
  if (addBtn) addBtn.disabled = activeCount === 0 || state.errors.length > 0;
}

/** Zeigt/versteckt die BOM-Section — erst nach Anwendung + Ø + Kaminhöhe */
function toggleBomVisibility() {
  const bomSection = document.getElementById('ew-bom-section');
  if (!bomSection) return;
  const ready = state.anwendung && state.diameter && state.kaminhoehe;
  bomSection.style.display = ready ? 'block' : 'none';
}

// ══════════════════════════════════════════════════════════════════
// H. ACTIONS
// ══════════════════════════════════════════════════════════════════

/**
 * Aktualisiert einen State-Key und triggert Neuberechnung.
 * Respektiert userTouched-Mengen.
 * @param {string} key
 * @param {*} value
 */
function updateState(key, value) {
  if (!(key in state)) {
    console.warn('[EW] updateState: unbekannter Key:', key);
    return;
  }
  state[key] = value;
  recompute();
}

/** Validiert, baut die BOM neu und rendert alles — ohne State-Key zu ändern */
function recompute() {
  // Validierung
  const { errors, warnings, infos } = validateState(state);
  state.errors   = errors;
  state.warnings = warnings;
  state.infos    = infos;

  // BOM neuberechnen
  const newBom = buildBom();

  // userTouched-Mengen erhalten
  for (const newLine of newBom) {
    const existing = state.bom.find(l => l.key === newLine.key);
    if (existing && existing.userTouched) {
      newLine.quantity     = existing.quantity;
      newLine.userTouched  = true;
    }
  }
  state.bom = newBom;

  // Rendern
  renderAlerts({ errors, warnings, infos });
  renderBomTable(state.bom);
  renderSummary();
  toggleBomVisibility();
  updateFormDisplay(state);
  syncSectionModules(state); // E2: Abschnitts-Module (Skizzen, Rechenwerte)
}

/** Gesamter Konfigurator-Reset: alle Formularwerte + BOM auf Anfangszustand */
function resetAll() {
  // Formularwerte auf Initialwerte zurücksetzen — E2: EINE Quelle (createDefaultState),
  // keine zweite Liste mehr, die neue Felder vergessen könnte (drei Alt-Bugs sassen genau hier)
  Object.assign(state, createDefaultState(), { bom: [], errors: [], warnings: [], infos: [] });

  // Eingabefelder leeren (Kaminhöhe + alle generischen .ew-input[data-key])
  const khInput = document.getElementById('ew-input-kaminhoehe');
  if (khInput) khInput.value = '';
  document.querySelectorAll('#ew-form .ew-input[data-key]').forEach(inp => { inp.value = ''; });

  // UI komplett neu aufbauen
  updateFormDisplay(state);
  syncSectionModules(state);
  renderAlerts({ errors: [], warnings: [], infos: [] });
  renderBomTable([]);
  renderSummary();
  toggleBomVisibility();
}

/** Alle BOM-Positionen per Bulk-Add in den Warenkorb legen */
async function addAllToCart() {
  const items = state.bom
    .filter(line => line.variantId && line.quantity > 0)
    .map(line => line.properties
      ? { id: line.variantId, quantity: line.quantity, properties: line.properties }
      : { id: line.variantId, quantity: line.quantity });

  if (!items.length) {
    alert(t('keine_positionen'));
    return;
  }

  const btn = document.getElementById('ew-add-to-cart-btn');
  if (btn) { btn.disabled = true; btn.textContent = t('wird_hinzugefuegt'); }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || `HTTP ${res.status}`);
    }
    window.location.href = (window.EW_LOCALE === 'fr') ? '/fr/cart' : '/cart';
  } catch (e) {
    console.error('[EW] addAllToCart Fehler:', e);
    alert(t('fehler', { MSG: e.message }));
    if (btn) { btn.disabled = false; btn.textContent = t('alle_in_warenkorb'); }
  }
}

/** Einzelne Variante in den Warenkorb legen (Zeilen-Button) */
async function addSingleToCart(variantId, qty, btn, properties = null) {
  const origText = btn?.innerHTML;
  if (btn) { btn.style.opacity = '0.5'; btn.disabled = true; }

  try {
    const item = properties ? { id: variantId, quantity: qty, properties } : { id: variantId, quantity: qty };
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || `HTTP ${res.status}`);
    }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    if (btn) { btn.style.color = '#3cb371'; btn.style.opacity = '1'; }
  } catch (e) {
    console.error('[EW] addSingleToCart Fehler:', e);
    alert(`Fehler: ${e.message}`);
    if (btn) { btn.innerHTML = origText; btn.style.opacity = '1'; btn.disabled = false; }
  }
}

// ══════════════════════════════════════════════════════════════════
// I. FORM — Questions 1–10 (Sprint 2)
// ══════════════════════════════════════════════════════════════════

/**
 * Bindet alle Frage-Buttons und das Kaminhöhen-Input an updateState().
 * Läuft einmal beim Boot nach DOMContentLoaded.
 */
function initForm() {
  const form = document.getElementById('ew-form');
  if (!form) return;

  // ── Option-Buttons (alle data-key / data-value Buttons im Formular)
  form.querySelectorAll('.ew-option-btn[data-key]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('disabled')) return; // ausgegraute Optionen ignorieren

      const key   = btn.dataset.key;
      const rawVal = btn.dataset.value;

      // Boolean-Umwandlung für Ja/Nein-Buttons
      let value;
      if (rawVal === 'true')  value = true;
      else if (rawVal === 'false') value = false;
      else if (!isNaN(Number(rawVal)) && rawVal !== '') value = Number(rawVal);
      else value = rawVal;

      // Pellets-Restriktion: Ø >100mm blockieren
      if (key === 'diameter' && state.anwendung === 'pellets' && !btn.hasAttribute('data-pellets-ok')) {
        updateState('warnings', state.warnings); // trigger re-render only
        return;
      }

      updateState(key, value);
    });
  });

  // ── Kaminhöhe Input (text → debounced updateState)
  const kaminInput = document.getElementById('ew-input-kaminhoehe');
  if (kaminInput) {
    let debounce;
    kaminInput.addEventListener('input', () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        const raw = kaminInput.value.replace(',', '.');
        const val = parseFloat(raw);
        updateState('kaminhoehe', !isNaN(val) && val > 0 ? val : null);
      }, 350);
    });
  }

  // ── E2: generische Zahlenfelder der Abschnitts-Module (.ew-input[data-key], ohne Kaminhöhe)
  //    Muster wie oben: Debounce, Komma → Punkt, ungültig/≤ 0 → null
  //    data-pending markiert ein Feld zwischen Tastendruck und Übernahme — Module dürfen
  //    solche Felder nicht aus dem State zurückspiegeln (sonst geht die Eingabe verloren)
  form.querySelectorAll('.ew-input[data-key]').forEach(inp => {
    if (inp === kaminInput || !(inp.dataset.key in state)) return;
    let debounce;
    inp.addEventListener('input', () => {
      inp.dataset.pending = '1';
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        delete inp.dataset.pending;
        const val = parseFloat(inp.value.replace(',', '.'));
        updateState(inp.dataset.key, !isNaN(val) && val > 0 ? val : null);
      }, 350);
    });
  });
}

/**
 * Synchronisiert den visuellen Zustand des Formulars mit dem State-Objekt.
 * Wird nach jedem updateState()-Aufruf aufgerufen.
 * @param {object} s - aktueller State
 */
function updateFormDisplay(s) {
  const form = document.getElementById('ew-form');
  if (!form) return;

  // ── 1. Aktive Button-Zustände aktualisieren
  form.querySelectorAll('.ew-option-btn[data-key]').forEach(btn => {
    const key      = btn.dataset.key;
    const rawVal   = btn.dataset.value;
    const stateVal = s[key];

    let btnVal;
    if (rawVal === 'true')  btnVal = true;
    else if (rawVal === 'false') btnVal = false;
    else if (!isNaN(Number(rawVal)) && rawVal !== '') btnVal = Number(rawVal);
    else btnVal = rawVal;

    // Vergleich: Numbers und Strings müssen passen
    const isActive = (stateVal !== null && stateVal !== undefined)
      ? (String(stateVal) === String(btnVal))
      : false;

    btn.classList.toggle('active', isActive);
  });

  // ── 2. Conditional Visibility — E2: Regeln über evalCond() (25-modules.js);
  //    {key, eq} verhält sich exakt wie bisher, neu: neq/in/gte/lte/truthy/all/any/not
  form.querySelectorAll('[data-show-if]').forEach(el => {
    try {
      el.style.display = evalCond(JSON.parse(el.dataset.showIf), s) ? '' : 'none';
    } catch {}
  });

  form.querySelectorAll('[data-show-unless]').forEach(el => {
    try {
      el.style.display = !evalCond(JSON.parse(el.dataset.showUnless), s) ? '' : 'none';
    } catch {}
  });

  // ── 3. Pellets-Restriktion: Ø >100mm ausgrauen
  const pelletHint    = document.getElementById('ew-pellets-hint');
  const isPellets     = s.anwendung === 'pellets';
  const diameterBtns  = document.querySelectorAll('#ew-diameter-group .ew-option-btn');

  diameterBtns.forEach(btn => {
    const ok = !isPellets || btn.hasAttribute('data-pellets-ok');
    btn.classList.toggle('disabled', !ok);
  });

  if (pelletHint) pelletHint.style.display = isPellets ? '' : 'none';

  // Wenn Pellets gewählt und aktueller Ø nicht erlaubt → Ø zurücksetzen.
  // updateState statt Direkt-Mutation, damit auch BOM & Sichtbarkeit
  // neu berechnet werden (sonst bleibt eine veraltete Materialliste stehen).
  if (isPellets && s.diameter && s.diameter > 100) {
    updateState('diameter', null);
    return;
  }

  // ── 3b. Kaminabdeckplatte: nur bis Ø 300 mm verfügbar (keine 350er-SKUs)
  const kapHint        = document.getElementById('ew-kap-350-hint');
  const kapUnavailable = s.diameter === 350;
  form.querySelectorAll('.ew-option-btn[data-key="kaminabdeckplatte"]').forEach(b => {
    if (b.dataset.value === 'true') b.classList.toggle('disabled', kapUnavailable);
  });
  if (kapHint) kapHint.style.display = kapUnavailable ? '' : 'none';
  if (kapUnavailable && s.kaminabdeckplatte) {
    updateState('kaminabdeckplatte', false);
    return;
  }

  // ── 4. Kaminhöhe-Input: Vorschau Rohr-Kalkulation
  const pipesPreview = document.getElementById('ew-pipes-preview');
  if (pipesPreview) {
    if (s.kaminhoehe) {
      const is19mm = s.isolation === '19mm';
      const pipes  = calcPipes(s.kaminhoehe, is19mm);
      if (pipes) {
        const parts = [];
        if (pipes.n1000) parts.push(`${pipes.n1000}×1000mm`);
        if (pipes.n500)  parts.push(`${pipes.n500}×500mm`);
        if (pipes.n250)  parts.push(`${pipes.n250}×250mm`);
        pipesPreview.textContent = parts.length
          ? `→ ${parts.join(' + ')} ${t('pipe_einheit')}`
          : t('pipes_invalid');
        pipesPreview.style.display = '';
      } else {
        pipesPreview.style.display = 'none';
      }
    } else {
      pipesPreview.style.display = 'none';
    }
  }

  // ── 5. Russtopf-Sub-Label (je nach Anwendung)
  const russtopfSub = document.getElementById('ew-russtopf-sub');
  if (russtopfSub) {
    const needsKondensat = (s.anwendung === 'oel' || s.anwendung === 'gas');
    const label = needsKondensat ? t('mit_kondensatablauf') : '';
    russtopfSub.textContent = label;
    russtopfSub.style.display = label ? '' : 'none';
  }

  // ── 6. Leichtbauschacht-Frage: nur sichtbar bei Isolation 30mm / 50mm
  const lbsQ = document.getElementById('ew-q-leichtbauschacht');
  if (lbsQ) {
    const showLbs = s.isolation === '30mm' || s.isolation === '50mm';
    lbsQ.style.display = showLbs ? '' : 'none';
    // Wenn Isolation gewechselt wird → Leichtbauschacht zurücksetzen
    if (!showLbs && s.leichtbauschacht) {
      state.leichtbauschacht = false;
      // Nein-Button visuell aktivieren
      lbsQ.querySelectorAll('.ew-option-btn').forEach(b => {
        b.classList.toggle('active', b.dataset.value === 'false');
      });
    }
  }

  // ── 7. Sirius + Inox gebürstet — Button deaktivieren + Hinweis
  const surfaceHint = document.getElementById('ew-surface-hint');
  const inoxGebBtn  = document.getElementById('ew-surface-inox-gebuestet');
  const isSirius    = s.kaminhut === 'sirius';
  if (surfaceHint) surfaceHint.style.display = isSirius ? '' : 'none';
  if (inoxGebBtn)  inoxGebBtn.classList.toggle('disabled', isSirius);
  // Wenn Sirius gewählt und Inox gebürstet aktiv → Surface zurücksetzen
  if (isSirius && s.kaminhutSurface === 'inox-gebuestet') {
    state.kaminhutSurface = null;
    document.querySelectorAll('[data-key="kaminhutSurface"]').forEach(b => b.classList.remove('active'));
  }

  // ── 8. Stutzen-Frage: nur sichtbar wenn Kaminabdeckplatte = Ja
  const stutzenQ = document.getElementById('ew-q-kaminabdeckplatte-stutzen');
  if (stutzenQ) {
    stutzenQ.style.display = s.kaminabdeckplatte ? '' : 'none';
  }

  // ── 9. Dila rund & Kaminabdeckplatte — beide immer sichtbar
  const kapQ      = document.querySelector('[data-ew-question="kaminabdeckplatte"]');
  const dilaRundQ = document.getElementById('ew-q-dilarund');

  if (kapQ)      kapQ.style.display = '';
  if (dilaRundQ) dilaRundQ.style.display = '';
}

// ══════════════════════════════════════════════════════════════════
// J. TAB-SWITCHING & URL-SYNC
// ══════════════════════════════════════════════════════════════════

function initTabs() {
  // Tab-Buttons: #ew-tab-nav ist der nav in ew-tab-nav.liquid — sicher vorhanden
  const tabs = document.querySelectorAll('#ew-tab-nav .b2b-tab[data-tab]');
  if (!tabs.length) return;

  // Section-Wrapper per bekanntem Inner-Element finden:
  //   .closest('.shopify-section') ist Shopify-Standard und ID-format-unabhängig.
  //
  // Tab-nav Section → nextElementSibling = product-grid Section
  //                 → nextNextElementSibling = konfigurator Section
  const tabNavEl      = document.getElementById('ew-tab-nav');
  const tabNavSection = tabNavEl?.closest('.shopify-section') ?? null;
  const productGridEl = tabNavSection?.nextElementSibling ?? null;
  const konfigEl      = productGridEl?.nextElementSibling ?? null;



  function activateTab(tabName) {
    tabs.forEach(t => {
      const active = t.dataset.tab === tabName;
      t.classList.toggle('active', active);
      t.setAttribute('aria-selected', String(active));
    });
    if (productGridEl) productGridEl.style.display = tabName === 'produkte'     ? 'block' : 'none';
    if (konfigEl)      konfigEl.style.display      = tabName === 'konfigurator' ? 'block' : 'none';

    // URL-Sync via Hash — kein Shopify-Template-Konflikt beim Refresh
    history.replaceState(null, '', tabName === 'konfigurator' ? '#konfigurator' : '#');
  }

  tabs.forEach(tab => {
    tab.addEventListener('click', () => activateTab(tab.dataset.tab));
  });

  // Initial-Zustand: Hash oder Legacy ?view=konfigurator
  const hash   = window.location.hash;
  const legacy = new URLSearchParams(window.location.search).get('view');
  if (legacy === 'konfigurator') {
    // Bereinige alte ?view=konfigurator URL einmalig
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('view');
    cleanUrl.hash = 'konfigurator';
    history.replaceState(null, '', cleanUrl.toString());
  }
  activateTab((hash === '#konfigurator' || legacy === 'konfigurator') ? 'konfigurator' : 'produkte');
}


// ══════════════════════════════════════════════════════════════════
// J. MANUELLE SUCHE (Sprint 5)
// ══════════════════════════════════════════════════════════════════

function initManualSearch() {
  const input   = document.getElementById('ew-manual-search');
  const results = document.getElementById('ew-manual-results');
  if (!input || !results) return;

  let debounceTimer;

  input.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const q = input.value.trim();
      if (q.length < 2) { results.hidden = true; return; }

      const hits = skuIndex.search(q);
      if (!hits.length) { results.hidden = true; return; }

      results.innerHTML = hits.map(({ variant }) => `
        <div class="ew-search-result"
             role="option"
             data-product-id="${esc(variant.productId)}"
             data-variant-id="${esc(variant.id)}"
             data-product-title="${esc(variant.title)}"
             data-variant-title="${esc(variant.title)}"
             data-sku="${esc(variant.sku || '')}">
          <span class="ew-search-result__title">${esc(variant.title)}</span>
          <span class="ew-search-result__sku">${esc(variant.sku || '')}</span>
        </div>
      `).join('');
      results.hidden = false;

      results.querySelectorAll('.ew-search-result').forEach(row => {
        row.addEventListener('click', () => {
          const productId    = parseInt(row.dataset.productId, 10);
          const variantId    = parseInt(row.dataset.variantId, 10);
          const productTitle = row.dataset.productTitle;
          const variantTitle = row.dataset.variantTitle;
          const sku          = row.dataset.sku;

          state.bom.push(makeBomLine({
            key:               `manual_${variantId}`,
            role:              'manual',
            productId,
            variantId,
            title:             `${productTitle} (${variantTitle})`,
            sku,
            calculatedQuantity: 1,
            quantity:          1,
            userTouched:       true,
            reason:            t('manuell_hinzugefuegt'),
            optional:          true,
          }));

          renderBomTable(state.bom);
          renderSummary();
          toggleBomVisibility();

          input.value = '';
          results.hidden = true;
        });
      });
    }, 200);
  });

  // Klick ausserhalb schliessen
  document.addEventListener('click', e => {
    if (!input.contains(e.target) && !results.contains(e.target)) {
      results.hidden = true;
    }
  });
}

// ══════════════════════════════════════════════════════════════════
// K. BOOT
// ══════════════════════════════════════════════════════════════════

document.addEventListener('DOMContentLoaded', () => {
  // 1. Tabs initialisieren
  initTabs();

  // 2. Produkt-Index laden
  skuIndex.init();

  // 2b. Extra-Produkte jenseits des all_products-Limits nachladen (async);
  //     danach BOM neu berechnen, falls der Kunde schon konfiguriert hat
  skuIndex.fetchExtraProducts().then(() => recompute());

  // 3. Loading-State ausblenden, Form einblenden
  const loading = document.getElementById('ew-loading');
  const form    = document.getElementById('ew-form');
  if (loading) loading.style.display = 'none';
  if (form)    form.style.display    = 'block';

  // 3b. E2: Abschnitts-Module in ihre Liquid-Container rendern — VOR initForm,
  //     damit die Button-Bindung unten auch ihre Fragen erfasst
  renderSectionModules();

  // 4. Formular initialisieren (Button-Events + Input binden)
  initForm();

  // 5. Initiale Formular-Ansicht synchronisieren (Defaults aus State)
  updateFormDisplay(state);
  syncSectionModules(state);

  // 6. Summary initialisieren
  renderSummary();

  // 7. Manuelle Suche
  initManualSearch();

  // 8. BOM-Button-Events
  const addBtn   = document.getElementById('ew-add-to-cart-btn');
  const resetBtn = document.getElementById('ew-reset-btn');
  if (addBtn)   addBtn.addEventListener('click', addAllToCart);
  if (resetBtn) resetBtn.addEventListener('click', resetAll);
});

// ══════════════════════════════════════════════════════════════════
// E3. SVG-HELFER für technische Skizzen — Masslinien mit schrägen 45°-Endstrichen
// (Vorgabe Daniel, Muster aus dem Nach-Mass-Konfigurator). Klassen ew-svg-* im CSS.
// ══════════════════════════════════════════════════════════════════

/** Schräger Endstrich (45°) an einem Masslinien-Ende */
function svgTick(x, y, vertikal, cls) {
  return vertikal
    ? `<line class="${cls}" x1="${x - 5}" y1="${y + 4}" x2="${x + 5}" y2="${y - 4}"/>`
    : `<line class="${cls}" x1="${x - 4}" y1="${y + 5}" x2="${x + 4}" y2="${y - 5}"/>`;
}

/** Horizontale Masslinie y, von x1 bis x2, Beschriftung oberhalb (acc = Akzentfarbe) */
function svgHDim(x1, x2, y, label, acc) {
  const cls = 'ew-svg-dim' + (acc ? '-x' : '');
  let s = `<line class="${cls}" x1="${x1}" y1="${y}" x2="${x2}" y2="${y}"/>` + svgTick(x1, y, false, cls) + svgTick(x2, y, false, cls);
  if (label) s += `<text class="ew-svg-label${acc ? ' ew-svg-label--acc' : ''}" x="${(x1 + x2) / 2}" y="${y - 4}" text-anchor="middle">${esc(label)}</text>`;
  return s;
}

/** Vertikale Masslinie x, von y1 bis y2, Beschriftung rechts daneben */
function svgVDim(x, y1, y2, label, acc) {
  const cls = 'ew-svg-dim' + (acc ? '-x' : '');
  let s = `<line class="${cls}" x1="${x}" y1="${y1}" x2="${x}" y2="${y2}"/>` + svgTick(x, y1, true, cls) + svgTick(x, y2, true, cls);
  if (label) s += `<text class="ew-svg-label${acc ? ' ew-svg-label--acc' : ''}" x="${x + 6}" y="${(y1 + y2) / 2 + 4}">${esc(label)}</text>`;
  return s;
}

/** Hilfslinie (dünn, gepunktet) — z. B. Masshilfslinien vom Objekt zur Masslinie */
function svgAux(x1, y1, x2, y2) {
  return `<line class="ew-svg-aux" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"/>`;
}

// ══════════════════════════════════════════════════════════════════
// E3. ABSCHNITT 3 «Decken-/Dachdurchführung (Betonierung)» — Shop-Modul
//
// Fragefluss Daniel 18.09.2026 (Stand v0.45.3): «Betonierung? ja/nein», danach JE Decke/Dach eine Karte:
//   Decke/Dach flach/schräg → Neigung von Decke/Dach (nur schräg; Grad · Prozent · cm Gliedermeter) →
//   «Doppelfutterrohr zum Einbetonieren?» ja/nein → [bei Ja:] Ausführung Doppelfutterrohr flach/schräg (nur beim schrägen Dach, Vorschlag schräg) → System doppelwandig
//   (Airnox DW 32.5 / 50 / EI60, vorbelegt aus der Isolation von Punkt 1, änderbar) → mit Schleudern? → [Ja:] Höhe H · [Nein:] mit Klemmband?
//   (zusätzliches Klemmband als Verankerung statt Schleudern) → Masse → Säcke. Ab der zweiten Karte: Knopf «Wie Decke / Dach N».
//   Bei Nein: nur Masse → Säcke. «Einwandig/Doppelwandig» gibt es nicht mehr — das einwandige Doppelfutterrohr fragt Abschnitt 2 ab.
// am Ende «Decke / Dach hinzufügen» (dieselben Fragen erneut). Die Grundvarianten-Kacheln vom 17.09.
// sind entfernt (archiviert: airtec-b2b-theme/docs/konfigurator-skizzen/grundvarianten-kacheln/).
//
// Technik: Der Hauptschalter nutzt die generische Bindung (.ew-option-btn[data-key]). Die Karten sind
// dynamisch und binden sich SELBST per Delegation (data-bi = Index, data-bk = Feld, data-bv = Wert) —
// bewusst OHNE data-key, sonst würden initForm()/updateFormDisplay() sie als State-Schlüssel behandeln.
// Rechnung und Stückliste liegen in der Engine (createBetonierung, betonAuswahl, betonVolumen,
// buildMountingBom); hier nur Darstellung. Texte DE/FR im Modul — Theme-Locales sind tabu.
// Begriffe: «Ende Doppelfutterrohr» (nie «Scheibe»), FR «pattes» für Schleudern (wie im Shop).
// ══════════════════════════════════════════════════════════════════

(function () {
  const TXT = {
    de: {
      betonierung: 'Betonierung?', betonierung_sub: 'Decken-/Dachdurchführung einbetonieren',
      dfr: 'Doppelfutterrohr zum Einbetonieren?',
      ja: 'Ja', nein: 'Nein',
      karte: 'Decke / Dach __N__', entfernen: 'Entfernen', hinzufuegen: '+ Decke / Dach hinzufügen',
      form: 'Zu betonierende Decke / zu betonierendes Dach', form_flach: 'Flach', form_schraeg: 'Schräg',
      dfrform: 'Ausführung Doppelfutterrohr', dfr_flach: 'Flach', dfr_schraeg: 'Schräg',
      dfrform_vorschlag: 'Vorschlag passend zum schrägen Dach — änderbar',
      neigung: 'Neigung', art_grad: 'Grad', art_prozent: 'Prozent', art_cm: 'cm Gliedermeter',
      neigung_res: 'Neigung: __G__°',
      neigung_err_grad: 'Bitte eine Neigung zwischen 1 und 60° angeben.',
      neigung_err_prozent: 'Bitte eine Neigung zwischen 2 und 173 % angeben.',
      neigung_err_cm: 'Bitte einen Wert zwischen 68.6 und 77.0 cm angeben.',
      system: 'System doppelwandig', dw325: 'Airnox DW 32.5', dw50: 'Airnox DW 50', dw575: 'Airnox DW EI60',
      system_vorschlag: 'Vorschlag aus der Isolation (Punkt 1) — änderbar',
      ei60_hint: 'EI60 nur Ø 100–200 mm',
      schleudern: 'Mit Schleudern?', schleudern_sub: 'Verankerung im Beton (Schleudern/Mauerlaschen angeschweisst am externen Mantel des Doppelfutterrohrs)',
      klemmband: 'Mit Klemmband?', klemmband_sub: 'Verankerung im Beton mit einem zusätzlichen Klemmband statt angeschweisster Schleudern',
      wie: 'Wie Decke / Dach __N__',
      schleudern_erzwungen: 'Diese Bauform gibt es in diesem System nur mit Schleudern',
      hoehe: 'Höhe H der Schleudern', hoehe_sub: 'ab Ende Doppelfutterrohr — Standard 120 mm, möglich 30 bis 250 mm',
      hoehe_err: 'Bitte eine Höhe H zwischen 30 und 250 mm angeben.',
      masse: 'Masse der Betonierung', h: 'Dicke h', b: 'Breite b', d: 'Tiefe d',
      oben: 'Draufsicht', seite: 'Seitenansicht', ende: 'Ende Doppelfutterrohr',
      bedarf: 'Betonbedarf', bedarf_hint: 'Masse h, b, d eingeben — dann rechnet der Konfigurator Volumen und Säcke',
      bedarf_result: '≈ __V__ m³ · __N__ Säcke', bedarf_sub: '__PRO__ Säcke à 25 l je m³, Aussen-Ø Rohr __A__ mm abgezogen',
      bedarf_fehler: 'Das Rohr (Aussen-Ø __A__ mm) passt nicht in b × d',
      total: 'Betonbedarf gesamt', total_result: '≈ __V__ m³ · __N__ Säcke für __ANZ__ Betonierung(en)',
    },
    fr: {
      betonierung: 'Bétonnage ?', betonierung_sub: 'Bétonner le passage de dalle / de toiture',
      dfr: 'Manchette double à bétonner ?',
      ja: 'Oui', nein: 'Non',
      karte: 'Dalle / toit __N__', entfernen: 'Supprimer', hinzufuegen: '+ Ajouter une dalle / un toit',
      form: 'Dalle / toit à bétonner', form_flach: 'Plat', form_schraeg: 'Incliné',
      dfrform: 'Exécution manchette double', dfr_flach: 'Plate', dfr_schraeg: 'Inclinée',
      dfrform_vorschlag: 'Proposé d’après le toit incliné — modifiable',
      neigung: 'Inclinaison', art_grad: 'Degrés', art_prozent: 'Pour cent', art_cm: 'cm mètre pliant',
      neigung_res: 'Inclinaison : __G__°',
      neigung_err_grad: 'Veuillez indiquer une inclinaison entre 1 et 60°.',
      neigung_err_prozent: 'Veuillez indiquer une inclinaison entre 2 et 173 %.',
      neigung_err_cm: 'Veuillez indiquer une valeur entre 68.6 et 77.0 cm.',
      system: 'Système double paroi', dw325: 'Airnox DP 32.5', dw50: 'Airnox DP 50', dw575: 'Airnox DP EI60',
      system_vorschlag: 'Proposé d’après l’isolation (point 1) — modifiable',
      ei60_hint: 'EI60 seulement Ø 100–200 mm',
      schleudern: 'Avec pattes ?', schleudern_sub: 'Ancrage dans le béton (pattes de scellement soudées sur l’enveloppe extérieure de la manchette double)',
      klemmband: 'Avec bride ?', klemmband_sub: 'Ancrage dans le béton par une bride supplémentaire au lieu des pattes soudées',
      wie: 'Comme dalle / toit __N__',
      schleudern_erzwungen: 'Cette forme n’existe qu’avec pattes dans ce système',
      hoehe: 'Hauteur H des pattes', hoehe_sub: 'depuis l’extrémité de la manchette double — standard 120 mm, possible de 30 à 250 mm',
      hoehe_err: 'Veuillez indiquer une hauteur H entre 30 et 250 mm.',
      masse: 'Dimensions du bétonnage', h: 'Épaisseur h', b: 'Largeur b', d: 'Profondeur d',
      oben: 'Vue de dessus', seite: 'Vue de côté', ende: 'Extrémité de la manchette double',
      bedarf: 'Besoin en béton', bedarf_hint: 'Saisir h, b, d — le configurateur calcule volume et sacs',
      bedarf_result: '≈ __V__ m³ · __N__ sacs', bedarf_sub: '__PRO__ sacs de 25 l par m³, Ø ext. tuyau __A__ mm déduit',
      bedarf_fehler: 'Le tuyau (Ø ext. __A__ mm) ne rentre pas dans b × d',
      total: 'Besoin total en béton', total_result: '≈ __V__ m³ · __N__ sacs pour __ANZ__ bétonnage(s)',
    },
  };
  let L = TXT.de;
  const tx = (k, vars) => {
    let s = L[k] ?? TXT.de[k] ?? k;
    if (vars) for (const [n, v] of Object.entries(vars)) s = s.replace(new RegExp(`__${n.toUpperCase()}__`, 'g'), v);
    return s;
  };
  const num = (v) => { const n = Number(String(v ?? '').replace(',', '.')); return n > 0 ? n : null; };

  // ── Bausteine einer Karte ──────────────────────────────────────────
  const btn = (i, k, v, label, extra = '') => `<button class="ew-option-btn" type="button" data-bi="${i}" data-bk="${k}" data-bv="${v}"${extra}>${esc(label)}</button>`;
  const yesNo = (i, k) => `<div class="ew-btn-group ew-btn-group--yesno">${btn(i, k, 'true', tx('ja'))}${btn(i, k, 'false', tx('nein'))}</div>`;
  const numInput = (i, k, unit, placeholder = '') => `
    <div class="ew-input-wrap ew-input-wrap--sm">
      <input type="text" class="ew-input" data-bi="${i}" data-bk="${k}" inputmode="decimal" autocomplete="off" placeholder="${esc(placeholder)}">
      <span class="ew-input__unit" data-bu="${k}">${esc(unit)}</span>
    </div>`;
  const frage = (name, label, sub, inner) => `
      <div class="ew-question" data-bq="${name}">
        <p class="ew-question__label">${esc(label)}${sub ? ` <span class="ew-question__sub">${esc(sub)}</span>` : ''}</p>
        ${inner}
      </div>`;

  function card(i) {
    return `
<div class="ew-beton-card" data-ew-beton-card="${i}">
  <div class="ew-beton-card__head">
    <h4 class="ew-beton-card__title">${esc(tx('karte', { N: i + 1 }))}</h4>
    <div class="ew-beton-card__actions">
      ${i > 0 ? `<button type="button" class="ew-beton-card__copy" data-ew-beton-copy="${i}">${esc(tx('wie', { N: i }))}</button>` : ''}
      <button type="button" class="ew-beton-card__del" data-ew-beton-del="${i}">${esc(tx('entfernen'))}</button>
    </div>
  </div>
  <div class="ew-beton__grid">
    <div class="ew-beton__col">
      ${frage('form', tx('form'), '', `<div class="ew-btn-group">${btn(i, 'form', 'flach', tx('form_flach'))}${btn(i, 'form', 'schraeg', tx('form_schraeg'))}</div>`)}
      ${frage('neigung', tx('neigung'), '', `
        <div class="ew-btn-group ew-btn-group--wrap ew-beton__art">${btn(i, 'neigungArt', 'grad', tx('art_grad'))}${btn(i, 'neigungArt', 'prozent', tx('art_prozent'))}${btn(i, 'neigungArt', 'cm', tx('art_cm'))}</div>
        ${numInput(i, 'neigung', '°', '')}
        <p class="ew-beton__neigung-res" data-b-out="neigung"></p>
        <p class="ew-question__hint ew-question__hint--warning" data-b-err="neigung" style="display:none;"></p>`)}
      ${frage('dfr', tx('dfr'), '', yesNo(i, 'dfr'))}
      ${frage('dfrform', tx('dfrform'), '', `<div class="ew-btn-group">${btn(i, 'dfrForm', 'flach', tx('dfr_flach'))}${btn(i, 'dfrForm', 'schraeg', tx('dfr_schraeg'))}</div>
        <p class="ew-question__hint" data-b-err="dfrvorschlag" style="display:none;">${esc(tx('dfrform_vorschlag'))}</p>`)}
      ${frage('system', tx('system'), '', `
        <div class="ew-btn-group ew-btn-group--wrap">${btn(i, 'system', 'dw325', tx('dw325'))}${btn(i, 'system', 'dw50', tx('dw50'))}${btn(i, 'system', 'dw575', tx('dw575'), ' data-b-ei60')}</div>
        <p class="ew-question__hint" data-b-err="vorschlag" style="display:none;">${esc(tx('system_vorschlag'))}</p>
        <p class="ew-question__hint ew-question__hint--warning" data-b-err="ei60" style="display:none;">${esc(tx('ei60_hint'))}</p>`)}
      ${frage('schleudern', tx('schleudern'), tx('schleudern_sub'), `${yesNo(i, 'schleudern')}
        <p class="ew-question__hint" data-b-err="erzwungen" style="display:none;">${esc(tx('schleudern_erzwungen'))}</p>`)}
      ${frage('klemmband', tx('klemmband'), tx('klemmband_sub'), yesNo(i, 'klemmband'))}
      ${frage('hoehe', tx('hoehe'), tx('hoehe_sub'), `${numInput(i, 'hoehe', 'mm', '120')}
        <p class="ew-question__hint ew-question__hint--warning" data-b-err="hoehe" style="display:none;">${esc(tx('hoehe_err'))}</p>`)}
    </div>

    <div class="ew-beton__col" data-bq="masse">
      <div class="ew-question">
        <p class="ew-question__label">${esc(tx('masse'))}</p>
        <div class="ew-beton__masse">
          <label class="ew-beton__mass"><span>${esc(tx('h'))}</span>${numInput(i, 'h', 'mm', '250')}</label>
          <label class="ew-beton__mass"><span>${esc(tx('b'))}</span>${numInput(i, 'b', 'mm', '400')}</label>
          <label class="ew-beton__mass"><span>${esc(tx('d'))}</span>${numInput(i, 'd', 'mm', '400')}</label>
        </div>
      </div>
      <div class="ew-question">
        <p class="ew-question__label">${esc(tx('bedarf'))}</p>
        <p class="ew-beton__result" data-b-out="bedarf"></p>
      </div>
    </div>

    <div class="ew-beton__col ew-beton__sketches" data-bq="skizzen">
      <div class="ew-sketch"><p class="ew-sketch__title">${esc(tx('seite'))}</p><div data-b-sketch="seite"></div></div>
      <div class="ew-sketch"><p class="ew-sketch__title">${esc(tx('oben'))}</p><div data-b-sketch="oben"></div></div>
    </div>
  </div>
</div>`;
  }

  function questions(locale) {
    L = TXT[locale] || TXT.de;
    return `
<div class="ew-beton">
  <div class="ew-question" data-ew-question="betonierung">
    <p class="ew-question__label">${esc(tx('betonierung'))} <span class="ew-question__sub">${esc(tx('betonierung_sub'))}</span></p>
    <div class="ew-btn-group ew-btn-group--yesno">
      <button class="ew-option-btn" data-key="betonierung" data-value="true"  type="button">${esc(tx('ja'))}</button>
      <button class="ew-option-btn" data-key="betonierung" data-value="false" type="button">${esc(tx('nein'))}</button>
    </div>
  </div>
  <div data-show-if='{"key":"betonierung","eq":true}'>
    <div data-ew-beton-list></div>
    <div class="ew-beton__foot">
      <button type="button" class="ew-beton__add" data-ew-beton-add>${esc(tx('hinzufuegen'))}</button>
      <p class="ew-beton__total" data-ew-beton-total></p>
    </div>
  </div>
</div>`;
  }

  // ── Bindung per Delegation (Karten kommen und gehen) ────────────────
  function artUmrechnen(b, nachArt) {
    // Wert beim Wechsel der Eingabeart mitnehmen (zwei Dezimalen ⇒ praktisch keine Rundungsdrift)
    const g = betonNeigungGrad(b);
    b.neigungArt = nachArt;
    if (g === null) return;
    if (nachArt === 'grad') b.neigung = g;
    else if (nachArt === 'prozent') b.neigung = Math.round(gradZuProzent(g) * 100) / 100;
    else {
      let k = -1;
      for (let i = 0; i < METERWINKEL_GRAD.length - 1; i++) if (g >= METERWINKEL_GRAD[i] && g <= METERWINKEL_GRAD[i + 1]) { k = i; break; }
      b.neigung = k < 0 ? null : Math.round((METERWINKEL_CM0 + (k + (g - METERWINKEL_GRAD[k]) / (METERWINKEL_GRAD[k + 1] - METERWINKEL_GRAD[k])) * 0.1) * 100) / 100;
    }
  }

  function bind(root) {
    root.addEventListener('click', (ev) => {
      const add = ev.target.closest('[data-ew-beton-add]');
      if (add) {
        const liste = state.betonierungen;
        liste.push(createBetonierung(liste[liste.length - 1] || null));
        recompute();
        return;
      }
      const copy = ev.target.closest('[data-ew-beton-copy]');
      if (copy) {
        const i = Number(copy.dataset.ewBetonCopy);
        if (state.betonierungen[i] && state.betonierungen[i - 1]) state.betonierungen[i] = { ...state.betonierungen[i - 1] };
        recompute();
        return;
      }
      const del = ev.target.closest('[data-ew-beton-del]');
      if (del) {
        state.betonierungen.splice(Number(del.dataset.ewBetonDel), 1);
        if (!state.betonierungen.length) state.betonierungen.push(createBetonierung());
        recompute();
        return;
      }
      const b = ev.target.closest('.ew-option-btn[data-bk]');
      if (!b || b.classList.contains('disabled')) return;
      const eintrag = state.betonierungen[Number(b.dataset.bi)];
      if (!eintrag) return;
      const k = b.dataset.bk, raw = b.dataset.bv;
      if (k === 'neigungArt') artUmrechnen(eintrag, raw);
      else eintrag[k] = raw === 'true' ? true : raw === 'false' ? false : raw;
      recompute();
    });

    // Zahlenfelder: Debounce wie die generische Bindung, data-pending schützt vor dem Zurückspiegeln
    const timer = new WeakMap();
    root.addEventListener('input', (ev) => {
      const inp = ev.target.closest('.ew-input[data-bk]');
      if (!inp) return;
      inp.dataset.pending = '1';
      clearTimeout(timer.get(inp));
      timer.set(inp, setTimeout(() => {
        delete inp.dataset.pending;
        const eintrag = state.betonierungen[Number(inp.dataset.bi)];
        if (!eintrag) return;
        const val = parseFloat(String(inp.value).replace(',', '.'));
        eintrag[inp.dataset.bk] = !isNaN(val) && val > 0 ? val : null;
        recompute();
      }, 350));
    });
  }

  // ── Skizzen ───────────────────────────────────────────────────────
  function sketchOben(e, dAussen) {
    const b = num(e.b), d = num(e.d);
    const W = 300, H = 232, cx = 118, cy = 104;
    const bb = b || 400, dd = d || 320;
    const k = 140 / Math.max(bb, dd, (dAussen || 150) * 1.4);
    const rw = bb * k, rh = dd * k, r = (dAussen || 150) * k / 2;
    const x = cx - rw / 2, y = cy - rh / 2;
    let svg = `<svg viewBox="0 0 ${W} ${H}">`;
    svg += `<rect class="${b && d ? 'ew-svg-plate' : 'ew-svg-placeholder'}" x="${x}" y="${y}" width="${rw}" height="${rh}"/>`;
    svg += `<circle class="ew-svg-hole" cx="${cx}" cy="${cy}" r="${r}"/>`;
    svg += `<line class="ew-svg-center" x1="${cx - r - 8}" y1="${cy}" x2="${cx + r + 8}" y2="${cy}"/><line class="ew-svg-center" x1="${cx}" y1="${cy - r - 8}" x2="${cx}" y2="${cy + r + 8}"/>`;
    svg += svgAux(x, y, x, y - 18) + svgAux(x + rw, y, x + rw, y - 18);
    svg += svgHDim(x, x + rw, y - 12, b ? `b = ${b} mm` : 'b', true);
    svg += svgAux(x + rw, y, x + rw + 18, y) + svgAux(x + rw, y + rh, x + rw + 18, y + rh);
    svg += svgVDim(x + rw + 12, y, y + rh, d ? `d = ${d} mm` : 'd', true);
    // Ø als Masslinie UNTER der Betonfläche — nicht auf der Zeichnung (Daniel 18.09.)
    const yD = y + rh + 26;
    svg += svgAux(cx - r, cy, cx - r, yD + 6) + svgAux(cx + r, cy, cx + r, yD + 6);
    svg += svgHDim(cx - r, cx + r, yD, `Ø ${dAussen || '—'} mm`, false);
    return svg + '</svg>';
  }

  function sketchSeite(e, wahl, dAussen) {
    // Wie die Zeichnungen der Produktseite (Theme-Snippet b2b-kundenmasse): Decke/Dach als Parallelogramm,
    // Doppelfutterrohr senkrecht, doppelwandig bei DW, steht unter der Decke leicht vor und ist parallel zur
    // Decke geschnitten; dicker Strich = Ende Doppelfutterrohr. H = senkrecht an der Rohrwand vom Rohrende
    // bis zur Schleuder (bei schräg links UND rechts), Schleudern liegen parallel zur Decke.
    const h = num(e.h);
    const schraeg = wahl.form === 'schraeg';
    const a = schraeg ? Math.min(wahl.neigung === null ? 20 : wahl.neigung, 35) : 0;
    const t = Math.tan(a * Math.PI / 180);
    const W = 380, H = schraeg ? 270 : 220, cx = 160, x0 = 56, x1 = 264;   // rechts Platz für «h = 250»
    const hs = h ? Math.max(40, Math.min(84, h * 0.3)) : 64;
    const cy = schraeg ? 128 : 100;
    const pw = Math.max(20, Math.min(60, (dAussen || 150) * 0.24));
    const xl = cx - pw / 2, xr = cx + pw / 2, wall = 6, gap = 14, top = 8, Ls = 16;
    // Rohrende und Schleudern folgen der AUSFÜHRUNG des Rohrs: schräg ⇒ parallel zum Dach, flach ⇒ waagrecht (auch im schrägen Dach)
    const rohrSchraeg = wahl.dfrForm === 'schraeg', te = rohrSchraeg ? t : 0;
    const yT = (x) => cy - hs / 2 + (x - cx) * t, yB = (x) => yT(x) + hs;
    const yE = rohrSchraeg ? ((x) => yB(x) + gap) : (() => Math.max(yB(xl), yB(xr)) + gap);
    let svg = `<svg viewBox="0 0 ${W} ${H}">`;
    svg += `<polygon class="${h ? 'ew-svg-slab' : 'ew-svg-placeholder'}" points="${x0},${yT(x0)} ${x1},${yT(x1)} ${x1},${yB(x1)} ${x0},${yB(x0)}"/>`;
    if (wahl.dfr === false) {
      svg += `<rect class="ew-svg-pipe-fill" x="${xl}" y="${top}" width="${pw}" height="${H - 2 * top}"/>`;
      svg += `<line class="ew-svg-pipe-line" x1="${xl}" y1="${top}" x2="${xl}" y2="${H - top}"/><line class="ew-svg-pipe-line" x1="${xr}" y1="${top}" x2="${xr}" y2="${H - top}"/>`;
    } else {
      const pts = `${xl},${top} ${xl},${yE(xl)} ${xr},${yE(xr)} ${xr},${top}`;
      svg += `<polygon class="ew-svg-pipe-fill" points="${pts}"/><polyline class="ew-svg-pipe-line" points="${pts}"/>`;
      if (wall) {
        const il = xl + wall, ir = xr - wall;
        svg += `<polyline class="ew-svg-pipe-line" points="${il - 2},${top + 20} ${il},${top + 16} ${il},${yE(il)}"/>`;
        svg += `<polyline class="ew-svg-pipe-line" points="${ir + 2},${top + 20} ${ir},${top + 16} ${ir},${yE(ir)}"/>`;
      }
      svg += `<line class="ew-svg-disc" x1="${xl - 1}" y1="${yE(xl - 1)}" x2="${xr + 1}" y2="${yE(xr + 1)}"/>`;
      svg += `<text class="ew-svg-label ew-svg-label--muted" x="${cx}" y="${Math.max(yE(xl), yE(xr)) + 16}" text-anchor="middle">${esc(tx('ende'))}</text>`;
      if (wahl.klemmband) {
        const yk = (x) => (yT(x) + yB(x)) / 2 + (rohrSchraeg ? 0 : ((cx - x) * t));   // flaches Rohr: Band waagrecht
        svg += `<polygon class="ew-svg-band" points="${xl - 3},${yk(xl - 3) - 5} ${xr + 3},${yk(xr + 3) - 5} ${xr + 3},${yk(xr + 3) + 5} ${xl - 3},${yk(xl - 3) + 5}"/>`;
      }
      if (wahl.schleudern === true) {
        const hk = wahl.hoehe ? Math.max(gap + 6, Math.min(hs + gap - 6, wahl.hoehe * 0.3)) : 24;
        const yl = yE(xl) - hk, yr = yE(xr) - hk, cls = 'ew-svg-dim-x';
        svg += `<line class="ew-svg-lasche" x1="${xl}" y1="${yl}" x2="${xl - Ls}" y2="${yl - Ls * te}"/>`;
        svg += `<line class="ew-svg-lasche" x1="${xr}" y1="${yr}" x2="${xr + Ls}" y2="${yr + Ls * te}"/>`;
        const dxl = xl - 40;
        svg += svgAux(xl, yl, dxl, yl) + svgAux(xl, yE(xl), dxl, yE(xl));
        svg += `<line class="${cls}" x1="${dxl}" y1="${yl}" x2="${dxl}" y2="${yE(xl)}"/>` + svgTick(dxl, yl, true, cls) + svgTick(dxl, yE(xl), true, cls);
        svg += `<text class="ew-svg-label ew-svg-label--acc" x="${dxl - 8}" y="${(yl + yE(xl)) / 2 + 4}" text-anchor="end">${esc(wahl.hoehe ? `H = ${wahl.hoehe}` : 'H')}</text>`;
        if (rohrSchraeg) {
          const dxr = xr + 40;
          svg += svgAux(xr, yr, dxr, yr) + svgAux(xr, yE(xr), dxr, yE(xr));
          svg += `<line class="${cls}" x1="${dxr}" y1="${yr}" x2="${dxr}" y2="${yE(xr)}"/>` + svgTick(dxr, yr, true, cls) + svgTick(dxr, yE(xr), true, cls);
        }
      }
    }
    // Deckendicke h rechts, Neigung oben links
    svg += svgAux(x1, yT(x1), x1 + 52, yT(x1)) + svgAux(x1, yB(x1), x1 + 52, yB(x1));
    svg += svgVDim(x1 + 46, yT(x1), yB(x1), h ? `h = ${h}` : 'h', true);
    if (schraeg) {
      const r = 38, px = x0, py = yT(x0), rad = a * Math.PI / 180;
      svg += svgAux(px, py, px + 62, py);
      svg += `<path class="ew-svg-arc" d="M${px + r} ${py} A${r} ${r} 0 0 1 ${px + r * Math.cos(rad)} ${py + r * Math.sin(rad)}"/>`;
      svg += `<text class="ew-svg-label ew-svg-label--acc" x="${px}" y="${py - 7}">${esc(wahl.neigung === null ? 'α' : `α = ${wahl.neigung.toFixed(1)}°`)}</text>`;
    }
    return svg + '</svg>';
  }

  // ── Nachführen nach jedem updateFormDisplay ────────────────────────
  const show = (el, on) => { if (el) el.style.display = on ? '' : 'none'; };

  function sync(s, root) {
    if (!Array.isArray(s.betonierungen)) s.betonierungen = [];
    // Hauptschalter an und noch keine Decke erfasst ⇒ erste Karte anlegen (einmalig, danach ist die Liste nicht mehr leer)
    if (s.betonierung === true && !s.betonierungen.length) { s.betonierungen.push(createBetonierung()); recompute(); return; }

    const host = root.querySelector('[data-ew-beton-list]');
    if (!host) return;
    const liste = s.betonierungen;
    if (host.children.length !== liste.length) host.innerHTML = liste.map((_, i) => card(i)).join('');
    if (!s.betonierung) return;

    const d = Number(s.diameter);
    const ei60Ok = !d || DFR_BETON_DIAMETERS.dw575.includes(d);
    let volSumme = 0, mitMassen = 0;

    liste.forEach((e, i) => {
      const c = host.children[i];
      if (!c) return;
      const wahl = betonAuswahl(e, s);
      const q = (name) => c.querySelector(`[data-bq="${name}"]`);

      // Fragefluss: jede Frage erscheint erst, wenn die vorige beantwortet ist
      const mitRohr = wahl.form !== null && wahl.dfr === true;
      show(q('dfr'), wahl.form !== null);
      show(q('neigung'), wahl.form === 'schraeg');               // die Neigung gehört zu Decke/Dach — unabhängig vom Rohr
      show(q('dfrform'), mitRohr && wahl.form === 'schraeg');   // flache Decke ⇒ flaches Rohr, keine Nachfrage
      show(q('system'), mitRohr);
      show(q('schleudern'), mitRohr && !!wahl.ausf);
      show(q('hoehe'), mitRohr && !!wahl.ausf && wahl.schleudern === true);
      show(q('klemmband'), mitRohr && !!wahl.ausf && wahl.schleudern === false);   // Klemmband nur als Alternative zu den Schleudern
      show(q('masse'), wahl.form !== null && wahl.dfr !== null);
      show(q('skizzen'), wahl.form !== null);
      show(c.querySelector('[data-ew-beton-del]'), liste.length > 1);

      // Aktiv-Markierung der Knöpfe
      c.querySelectorAll('.ew-option-btn[data-bk]').forEach(b => {
        // System: aktiv ist die AUFGELÖSTE Wahl — also auch der Vorschlag aus der Isolation
        const v = b.dataset.bk === 'system' ? wahl.ausf : b.dataset.bk === 'dfrForm' ? wahl.dfrForm : e[b.dataset.bk];
        b.classList.toggle('active', v !== null && v !== undefined && String(v) === b.dataset.bv);
        b.classList.remove('disabled');
      });
      const ei60 = c.querySelector('[data-b-ei60]');
      if (ei60) ei60.classList.toggle('disabled', !ei60Ok);
      show(c.querySelector('[data-b-err="ei60"]'), !ei60Ok);
      show(c.querySelector('[data-b-err="vorschlag"]'), wahl.systemVorgeschlagen);
      show(c.querySelector('[data-b-err="dfrvorschlag"]'), wahl.dfrFormVorgeschlagen);
      if (wahl.schleudernErzwungen) c.querySelectorAll('.ew-option-btn[data-bk="schleudern"]').forEach(b => {
        b.classList.toggle('active', b.dataset.bv === 'true');
        b.classList.toggle('disabled', b.dataset.bv === 'false');
      });
      show(c.querySelector('[data-b-err="erzwungen"]'), wahl.schleudernErzwungen);

      // Zahlenfelder spiegeln (nicht während des Tippens)
      c.querySelectorAll('.ew-input[data-bk]').forEach(inp => {
        if (document.activeElement === inp || inp.dataset.pending) return;
        const v = e[inp.dataset.bk];
        inp.value = (v === null || v === undefined) ? '' : String(v);
      });

      // Neigung: Einheit, Ergebnis, Fehlermeldung
      const unit = c.querySelector('[data-bu="neigung"]');
      if (unit) unit.textContent = e.neigungArt === 'prozent' ? '%' : e.neigungArt === 'cm' ? 'cm' : '°';
      const res = c.querySelector('[data-b-out="neigung"]');
      if (res) res.textContent = (wahl.neigung !== null && e.neigungArt !== 'grad') ? tx('neigung_res', { G: wahl.neigung.toFixed(1) }) : '';
      const nErr = c.querySelector('[data-b-err="neigung"]');
      const nFalsch = wahl.form === 'schraeg' && num(e.neigung) !== null && wahl.neigung === null;
      if (nErr) { nErr.textContent = nFalsch ? tx('neigung_err_' + e.neigungArt) : ''; show(nErr, nFalsch); }
      show(c.querySelector('[data-b-err="hoehe"]'), mitRohr && wahl.schleudern === true && wahl.hoehe === null);

      // Volumen / Säcke je Betonierung
      const { dAussen, vol } = d ? betonVolumen(e, d, s) : { dAussen: null, vol: null };
      const out = c.querySelector('[data-b-out="bedarf"]');
      if (out) {
        if (vol) out.innerHTML = `${esc(tx('bedarf_result', { V: vol.toFixed(3), N: calcBetonSaecke(vol) }))}<small>${esc(tx('bedarf_sub', { PRO: BETON_SAECKE_PRO_M3, A: dAussen }))}</small>`;
        else if (dAussen && num(e.h) && num(e.b) && num(e.d)) out.innerHTML = `<small>${esc(tx('bedarf_fehler', { A: dAussen }))}</small>`;
        else out.innerHTML = `<small>${esc(tx('bedarf_hint'))}</small>`;
      }
      if (vol) { volSumme += vol; mitMassen += 1; }

      const seite = c.querySelector('[data-b-sketch="seite"]');
      const oben = c.querySelector('[data-b-sketch="oben"]');
      if (seite) seite.innerHTML = sketchSeite(e, wahl, dAussen);
      if (oben) oben.innerHTML = sketchOben(e, dAussen);
    });

    const total = root.querySelector('[data-ew-beton-total]');
    if (total) total.innerHTML = (mitMassen > 1)
      ? `<strong>${esc(tx('total'))}:</strong> ${esc(tx('total_result', { V: volSumme.toFixed(3), N: calcBetonSaecke(volSumme), ANZ: mitMassen }))}` : '';
  }

  registerSectionModule({ id: 'betonierung', questions, bind, sync });
})();
